/**
 * portfolio.js — Onboarding + Mi cartera
 *
 * Estado canónico durante el onboarding: localStorage.sr_onboarding
 * Sync con Make: stub `syncPortfolio()` — hoy persiste local, mañana POST a webhook
 *
 * Convenciones:
 * - Parseo de números en formato español (1.204,80 → 1204.80)
 * - Sin dependencias externas (vanilla JS)
 * - Bootstrap por `data-sr-page` en el wrapper de cada sección
 */
(function () {
  'use strict';

  // ─── Constantes ──────────────────────────────────────────────────────────
  var STORAGE_KEY = 'sr_onboarding';
  var STORAGE_VERSION = 1;
  var COVERAGE_URL = window.SR_COVERAGE_URL || '/cdn/shop/files/coverage.json';

  // ─── Estado / persistencia ───────────────────────────────────────────────
  function emptyState() {
    return {
      positions: [],
      coverage_requests: [],
      pending_sync: false,
      version: STORAGE_VERSION
    };
  }

  function loadState() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return emptyState();
      var s = JSON.parse(raw);
      if (!s || typeof s !== 'object') return emptyState();
      if (!Array.isArray(s.positions)) s.positions = [];
      if (!Array.isArray(s.coverage_requests)) s.coverage_requests = [];
      // Migración: recomputar derivados (realized_pl, totales) si faltan
      s.positions.forEach(function (p) {
        if (p.mode !== 'watch' && Array.isArray(p.transactions) && p.transactions.length > 0) {
          var r = recompute(p.transactions);
          if (p.realized_pl == null) p.realized_pl = r.realized_pl;
          if (p.total_buy_amount == null) p.total_buy_amount = r.total_buy_amount;
          if (p.total_sell_amount == null) p.total_sell_amount = r.total_sell_amount;
          if (p.sold_shares == null) p.sold_shares = r.sold_shares;
        }
      });
      s.version = STORAGE_VERSION;
      return s;
    } catch (_) {
      return emptyState();
    }
  }

  function saveState(s) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    } catch (_) {}
  }

  function clearState() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
  }

  // ─── Parsing y formato (ES) ──────────────────────────────────────────────
  /**
   * Parse de número en formato español, robusto al separador de miles.
   *
   * Convención (es-ES):
   *  - Coma = decimal · Punto = miles (uso canónico).
   *  - Coma + punto: punto siempre miles, coma siempre decimal.       1.204,80 → 1204.80
   *  - Solo coma: coma decimal.                                        16,80    → 16.80
   *  - Solo punto, varios puntos: todos miles.                         1.234.567→ 1234567
   *  - Solo punto, un único punto, último grupo con EXACTAMENTE 3 dígitos:
   *      → punto = miles.                                              1.680    → 1680
   *                                                                    16.800   → 16800
   *  - Solo punto, último grupo con 1, 2 o ≥4 dígitos: punto decimal.  16.80    → 16.80
   *                                                                    16.8     → 16.8
   *                                                                    16.8000  → 16.8
   *
   * Casos ambiguos (1.680 podría ser "1 689 cts" en raros locales) se resuelven
   * a favor de la convención ES porque es el formato que el usuario español
   * más probablemente teclea para un importe total.
   */
  function parseNumberES(v) {
    if (v == null) return NaN;
    if (typeof v === 'number') return v;
    var s = String(v).trim();
    if (!s) return NaN;
    // Conservar signo
    var negative = false;
    if (s.charAt(0) === '-') { negative = true; s = s.slice(1); }
    if (s.charAt(0) === '+') { s = s.slice(1); }
    // Espacios (a veces se usan como miles)
    s = s.replace(/\s+/g, '');

    var hasComma = s.indexOf(',') !== -1;
    var dotCount = (s.match(/\./g) || []).length;

    if (hasComma) {
      // Punto = miles, coma = decimal
      s = s.replace(/\./g, '').replace(',', '.');
    } else if (dotCount > 1) {
      // Varios puntos sin coma → todos miles
      s = s.replace(/\./g, '');
    } else if (dotCount === 1) {
      // Un único punto: discriminar entre decimal y miles
      var parts = s.split('.');
      var tail = parts[1] || '';
      if (tail.length === 3 && /^\d{3}$/.test(tail) && /^\d+$/.test(parts[0])) {
        // Último grupo de 3 dígitos → separador de miles (1.680, 16.800, 999.999)
        s = parts[0] + parts[1];
      }
      // En otro caso → decimal (ya tiene el punto correcto)
    }
    // Sin coma ni punto: ya es entero plano

    var n = parseFloat(s);
    if (isNaN(n)) return NaN;
    return negative ? -n : n;
  }

  // Sanity tests (solo en consola si window.SR_DEBUG)
  if (typeof window !== 'undefined' && window.SR_DEBUG) {
    var cases = [
      ['1.680', 1680], ['16.800', 16800], ['1.234.567', 1234567],
      ['1.204,80', 1204.80], ['16,80', 16.80], ['16.80', 16.80],
      ['16.8', 16.8], ['1680', 1680], ['-1.680', -1680], ['', NaN],
      ['1.5', 1.5], ['1.50', 1.50], ['1.500', 1500]
    ];
    var fails = cases.filter(function (c) {
      var got = parseNumberES(c[0]);
      return !(isNaN(c[1]) ? isNaN(got) : Math.abs(got - c[1]) < 1e-9);
    });
    if (fails.length) console.warn('[parseNumberES] FAIL', fails);
    else console.info('[parseNumberES] OK', cases.length, 'casos');
  }

  function formatEUR(n) {
    if (!isFinite(n)) return '—';
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  }

  // Formatea un importe con el símbolo de la moneda del producto (prefijo, igual que
  // los precios de escenario). Cada posición usa la moneda con la que se compró.
  function formatMoney(n, sym) {
    if (n == null || !isFinite(n)) return '—';
    sym = sym || '€';
    return sym + formatNumber(n, 2);
  }

  function formatNumber(n, dec) {
    if (dec == null) dec = 2;
    if (!isFinite(n)) return '—';
    return new Intl.NumberFormat('es-ES', { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(n);
  }

  function formatPct(n, dec) {
    if (dec == null) dec = 2;
    if (!isFinite(n)) return '—';
    var sign = n > 0 ? '+' : '';
    return sign + formatNumber(n, dec) + '%';
  }

  // ─── Cálculo de posición desde transacciones ────────────────────────────
  /**
   * Cálculo de posición usando método del precio medio ponderado (estándar broker ES):
   *  - avg_price = Σ importe_compras / Σ acciones_compradas
   *  - net_shares = Σ acciones_compradas − Σ acciones_vendidas
   *  - invested = net_shares × avg_price  (lo que sigues teniendo invertido en la posición actual)
   *  - realized_pl = Σ importe_ventas − (Σ acciones_vendidas × avg_price)  (P&L de las ventas pasadas)
   *  Cuando consultes el valor proyectado a futuro:
   *    valor_a_horizonte  = net_shares × precio_horizonte
   *    pl_latente_horiz   = valor_a_horizonte − invested
   *    pl_total_horiz     = pl_latente_horiz + realized_pl
   */
  function recompute(transactions) {
    var sumBuyShares = 0, sumBuyAmount = 0;
    var sumSellShares = 0, sumSellAmount = 0;
    for (var i = 0; i < transactions.length; i++) {
      var t = transactions[i];
      var s = parseNumberES(t.shares);
      var a = parseNumberES(t.amount);
      if (!isFinite(s) || s < 0) continue;
      if (t.type === 'buy') {
        sumBuyShares += s;
        if (isFinite(a) && a > 0) sumBuyAmount += a;
      } else if (t.type === 'sell') {
        sumSellShares += s;
        if (isFinite(a) && a > 0) sumSellAmount += a;
      }
    }
    var net = sumBuyShares - sumSellShares;
    var avg = sumBuyShares > 0 ? (sumBuyAmount / sumBuyShares) : 0;
    var invested = net > 0 ? net * avg : 0;
    var realized = sumSellAmount - (sumSellShares * avg);

    var r2 = function (n) { return Math.round(n * 100) / 100; };
    return {
      net_shares: Math.round(net * 1e6) / 1e6,
      avg_price: r2(avg),
      invested: r2(invested),
      total_buy_amount: r2(sumBuyAmount),
      total_sell_amount: r2(sumSellAmount),
      sold_shares: Math.round(sumSellShares * 1e6) / 1e6,
      realized_pl: r2(realized)
    };
  }

  // ─── Carga de productos: Storefront API + fallback coverage.json ─────────
  var _coverage = null;
  var _coveragePromise = null;
  var _coverageEnriched = false;
  var STOREFRONT_URL = (window.SR_STOREFRONT_URL || '').trim();
  var STOREFRONT_TOKEN = (window.SR_STOREFRONT_TOKEN || '').trim();
  // Series por ticker para la gráfica de escenarios: { ticker: { evolution, analystTargets } }.
  // Se rellena con una consulta dirigida SOLO a las posiciones (no al barrido completo).
  var POSITION_SERIES = {};
  var COVERAGE_CACHE_KEY = 'sr_coverage_cache_v3';
  var COVERAGE_CACHE_TTL = 30 * 60 * 1000; // 30 min

  // Limpia strings de precio con símbolos de divisa
  var CURRENCY_SYMS = ['HK$','HKD','&#8364;','€','$','£','₹','₩','¥','zł','PLN','kr','Rs','R$','CHF','SEK','NOK','DKK','INR','KRW','TWD','JPY','CNY','SGD','AUD','CAD'];
  function cleanPrice(v) {
    if (!v) return null;
    var s = String(v);
    for (var i = 0; i < CURRENCY_SYMS.length; i++) s = s.split(CURRENCY_SYMS[i]).join('');
    s = s.replace(/\s+/g, '').replace(/,/g, '');
    var n = parseFloat(s);
    return (isFinite(n) && n > 0) ? n : null;
  }
  function detectCurrency(v) {
    if (!v) return null;
    var s = String(v);
    var ordered = ['HK$','HKD','R$','SEK','NOK','DKK','CHF','INR','KRW','TWD','JPY','CNY','SGD','AUD','CAD','PLN','kr','Rs','€','£','₹','₩','¥','zł','$'];
    for (var i = 0; i < ordered.length; i++) if (s.indexOf(ordered[i]) !== -1) return ordered[i];
    return null;
  }

  function loadCoverage() {
    if (_coverage && _coverageEnriched) return Promise.resolve(_coverage);
    if (_coveragePromise) return _coveragePromise;

    // 1) Cache de sesión
    try {
      var raw = sessionStorage.getItem(COVERAGE_CACHE_KEY);
      if (raw) {
        var cached = JSON.parse(raw);
        if (cached && cached.ts && (Date.now() - cached.ts) < COVERAGE_CACHE_TTL && Array.isArray(cached.data)) {
          _coverage = cached.data;
          _coverageEnriched = true;
          return Promise.resolve(_coverage);
        }
      }
    } catch (_) {}

    _coveragePromise = (function () {
      // 2) Storefront API si tenemos token y URL
      if (STOREFRONT_URL && STOREFRONT_TOKEN) {
        return fetchStorefrontAll()
          .then(function (rich) {
            if (rich && rich.length > 0) {
              _coverage = rich;
              _coverageEnriched = true;
              try {
                sessionStorage.setItem(COVERAGE_CACHE_KEY, JSON.stringify({ ts: Date.now(), data: rich }));
              } catch (_) {}
              return rich;
            }
            return fallbackCoverageJson();
          })
          .catch(fallbackCoverageJson);
      }
      // 3) Sin token: directo al fallback
      return fallbackCoverageJson();
    })();
    return _coveragePromise;
  }

  function fallbackCoverageJson() {
    return fetch(COVERAGE_URL, { cache: 'force-cache' })
      .then(function (r) { return r.ok ? r.json() : []; })
      .then(function (data) {
        _coverage = Array.isArray(data) ? data : [];
        return _coverage;
      })
      .catch(function () { _coverage = []; return _coverage; });
  }

  // Storefront GraphQL: paginar hasta vaciar la colección "informes"
  function fetchStorefrontAll() {
    var collected = [];

    function pageQuery(after) {
      var q = ''
        + 'query($after: String) {'
        +   ' collection(handle: "informes") {'
        +     ' products(first: 50, after: $after) {'
        +       ' pageInfo { hasNextPage endCursor }'
        +       ' edges { node {'
        +         ' id handle title'
        +         ' metafields(identifiers: ['
        +           ' {namespace: "custom", key: "ticker_symbol"},'
        +           ' {namespace: "custom", key: "company_name"},'
        +           ' {namespace: "custom", key: "sector"},'
        +           ' {namespace: "custom", key: "country"},'
        +           ' {namespace: "custom", key: "overall_score"},'
        +           ' {namespace: "custom", key: "report_date"},'
        +           ' {namespace: "custom", key: "validated_tesis"},'
        +           ' {namespace: "custom", key: "tesis_validation"},'
        +           ' {namespace: "custom", key: "precio_actual"},'
        +           ' {namespace: "custom", key: "last_real_price"},'
        +           ' {namespace: "custom", key: "precio_base_6m"},'
        +           ' {namespace: "custom", key: "precio_base_12m"},'
        +           ' {namespace: "custom", key: "precio_base_24m"},'
        +           ' {namespace: "custom", key: "precio_pesimista_6m"},'
        +           ' {namespace: "custom", key: "precio_pesimista_12m"},'
        +           ' {namespace: "custom", key: "precio_pesimista_24m"},'
        +           ' {namespace: "custom", key: "precio_optimista_6m"},'
        +           ' {namespace: "custom", key: "precio_optimista_12m"},'
        +           ' {namespace: "custom", key: "precio_optimista_24m"},'
        +           ' {namespace: "custom", key: "tesis_summary"}'
        +         ' ]) { key value }'
        +       ' } }'
        +     ' }'
        +   ' }'
        + ' }';
      return fetch(STOREFRONT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
        },
        body: JSON.stringify({ query: q, variables: { after: after } })
      }).then(function (r) { return r.json(); });
    }

    function pump(after) {
      return pageQuery(after).then(function (resp) {
        var data = resp && resp.data && resp.data.collection && resp.data.collection.products;
        if (!data) throw new Error('Storefront API: empty response');
        data.edges.forEach(function (edge) { collected.push(edge.node); });
        if (data.pageInfo.hasNextPage) return pump(data.pageInfo.endCursor);
        return collected;
      });
    }

    return pump(null).then(function (nodes) { return nodes.map(transformProduct); });
  }

  // Consulta DIRIGIDA solo a las posiciones: trae la evolución de precio ("Precio Real")
  // y el consenso de analistas para dibujar sus líneas en la gráfica. En 2 pasos porque
  // el Storefront API NO acepta reverse/last en `references`:
  //   1) leer el `value` (array ordenado de GIDs) de price_evolutiom + analyst_targets,
  //   2) resolver por ID solo los últimos N metaobjetos (los más recientes) de cada lista.
  // Acotado (últimos 16 precios / 4 consensos) para no rozar el tope de metaobjetos.
  // Resiliente: ante cualquier error resuelve un mapa vacío (la gráfica se queda con los
  // 3 escenarios). Devuelve { ticker: { evolution, analystTargets } }.
  function fetchPositionSeries(productGids) {
    // Normaliza a GID completo: mi-cartera guarda product_gid como ID numérico (Liquid
    // product.id), mientras el onboarding lo trae del coverage como gid://…. nodes(ids:)
    // exige el GID completo o falla con "Invalid global id".
    productGids = (productGids || []).filter(Boolean).map(function (g) {
      g = String(g);
      return g.indexOf('gid://') === 0 ? g : ('gid://shopify/Product/' + g);
    });
    if (!productGids.length || !STOREFRONT_URL || !STOREFRONT_TOKEN) return Promise.resolve({});
    var PE_N = 16, AT_N = 4;

    function sf(query, variables) {
      return fetch(STOREFRONT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN },
        body: JSON.stringify({ query: query, variables: variables })
      }).then(function (r) { return r.json(); });
    }
    function parseGidList(v) {
      if (!v) return [];
      try { var a = JSON.parse(v); return Array.isArray(a) ? a : []; } catch (_) { return []; }
    }
    function fval(f) { return (f && f.value) || null; }

    // Paso 1: value (array de GIDs de metaobjetos) por producto.
    var q1 = 'query($ids:[ID!]!){ nodes(ids:$ids){ ... on Product {'
      + ' tk: metafield(namespace:"custom", key:"ticker_symbol"){ value }'
      + ' pe: metafield(namespace:"custom", key:"price_evolutiom"){ value }'
      + ' at: metafield(namespace:"custom", key:"analyst_targets"){ value }'
      + ' } } }';

    return sf(q1, { ids: productGids }).then(function (r1) {
      var nodes = (r1 && r1.data && r1.data.nodes) || [];
      var byTicker = {};
      var moGids = [];   // GIDs de metaobjetos a resolver (orden = moMeta)
      var moMeta = [];   // { t: ticker, k: 'pe'|'at' } paralelo a moGids
      nodes.forEach(function (n) {
        if (!n) return;
        var ticker = (n.tk && n.tk.value) || '';
        if (!ticker) return;
        byTicker[ticker] = { evolution: [], analystTargets: [] };
        parseGidList(n.pe && n.pe.value).slice(-PE_N).forEach(function (g) { moGids.push(g); moMeta.push({ t: ticker, k: 'pe' }); });
        parseGidList(n.at && n.at.value).slice(-AT_N).forEach(function (g) { moGids.push(g); moMeta.push({ t: ticker, k: 'at' }); });
      });
      if (!moGids.length) return byTicker;

      // Paso 2: resolver los metaobjetos por ID (nodes preserva el orden de entrada).
      var q2 = 'query($ids:[ID!]!){ nodes(ids:$ids){ ... on Metaobject {'
        + ' d: field(key:"date"){ value }'
        + ' p: field(key:"price"){ value }'
        + ' pr: field(key:"is_promising"){ value }'
        + ' fa: field(key:"is_failing"){ value }'
        + ' st: field(key:"is_promising_stop"){ value }'
        + ' am: field(key:"target_mean"){ value }'
        + ' ah: field(key:"target_high"){ value }'
        + ' al: field(key:"target_low"){ value }'
        + ' an: field(key:"num_analysts"){ value }'
        + ' ac: field(key:"current_price"){ value }'
        + ' } } }';

      // Trocear en lotes (límite práctico de nodes(ids:)).
      var chunks = [];
      for (var i = 0; i < moGids.length; i += 200) {
        chunks.push({ ids: moGids.slice(i, i + 200), meta: moMeta.slice(i, i + 200) });
      }

      return Promise.all(chunks.map(function (c) {
        return sf(q2, { ids: c.ids }).then(function (r2) {
          var ns = (r2 && r2.data && r2.data.nodes) || [];
          ns.forEach(function (m, idx) {
            if (!m) return;
            var meta = c.meta[idx];
            var bucket = meta && byTicker[meta.t];
            if (!bucket) return;
            if (meta.k === 'pe') {
              bucket.evolution.push({ date: fval(m.d), price: fval(m.p), is_promising: fval(m.pr), is_failing: fval(m.fa), is_promising_stop: fval(m.st) });
            } else {
              bucket.analystTargets.push({ date: fval(m.d), target_mean: fval(m.am), target_high: fval(m.ah), target_low: fval(m.al), num_analysts: fval(m.an), current_price: fval(m.ac) });
            }
          });
        });
      })).then(function () { return byTicker; });
    }).catch(function () { return {}; });
  }

  // Transforma nodo GraphQL → objeto plano con los datos que usamos en client
  function transformProduct(node) {
    var mfs = {};
    if (Array.isArray(node.metafields)) {
      node.metafields.forEach(function (m) { if (m) mfs[m.key] = m.value; });
    }
    var precioRaw = mfs.precio_actual || mfs.last_real_price || '';
    return {
      ticker: mfs.ticker_symbol || '',
      handle: node.handle,
      product_gid: node.id,
      name: mfs.company_name || (node.title || '').replace(/ - Informe de actividad operativa$/, ''),
      sector: mfs.sector || null,
      country: mfs.country || null,
      score: mfs.overall_score ? parseInt(mfs.overall_score, 10) : null,
      report_date: mfs.report_date || null,
      validated_tesis: mfs.validated_tesis || 'Base',
      tesis_validation: mfs.tesis_validation || 'Pendiente',
      currency: detectCurrency(precioRaw),
      precio_actual: cleanPrice(mfs.precio_actual),
      last_real_price: cleanPrice(mfs.last_real_price),
      precio_base_6m: cleanPrice(mfs.precio_base_6m),
      precio_base_12m: cleanPrice(mfs.precio_base_12m),
      precio_base_24m: cleanPrice(mfs.precio_base_24m),
      precio_pesimista_6m: cleanPrice(mfs.precio_pesimista_6m),
      precio_pesimista_12m: cleanPrice(mfs.precio_pesimista_12m),
      precio_pesimista_24m: cleanPrice(mfs.precio_pesimista_24m),
      precio_optimista_6m: cleanPrice(mfs.precio_optimista_6m),
      precio_optimista_12m: cleanPrice(mfs.precio_optimista_12m),
      precio_optimista_24m: cleanPrice(mfs.precio_optimista_24m),
      tesis_summary: mfs.tesis_summary || ''
    };
  }

  // Helper: primeras N palabras (recorta sin partir palabras, añade …)
  function firstWords(text, n) {
    if (!text) return '';
    var clean = String(text).replace(/\s+/g, ' ').trim();
    if (!clean) return '';
    var words = clean.split(' ');
    if (words.length <= n) return clean;
    return words.slice(0, n).join(' ') + '…';
  }

  // ─── Cálculos de oportunidad y asimetría (replicar product-table-row.liquid) ─
  function computeOpp(p) {
    if (!p || !p.precio_base_6m || !p.precio_optimista_6m || !p.precio_pesimista_6m) return null;
    var b6 = p.precio_base_6m, b12 = p.precio_base_12m, b24 = p.precio_base_24m;
    var bu6 = p.precio_optimista_6m, bu12 = p.precio_optimista_12m, bu24 = p.precio_optimista_24m;
    var br6 = p.precio_pesimista_6m, br12 = p.precio_pesimista_12m, br24 = p.precio_pesimista_24m;
    if (!b6 || !bu6 || !br6) return null;
    var u6 = (bu6 - b6) / b6, d6 = (b6 - br6) / b6;
    var u12 = (b12 && bu12) ? (bu12 - b12) / b12 : 0;
    var d12 = (b12 && br12) ? (b12 - br12) / b12 : 0;
    var u24 = (b24 && bu24) ? (bu24 - b24) / b24 : 0;
    var d24 = (b24 && br24) ? (b24 - br24) / b24 : 0;
    var aUp = 6 * u6 + 9 * u12 + 6 * u24;
    var aDn = 6 * d6 + 9 * d12 + 6 * d24;
    if (aDn <= 0) return null;
    var ratio = aUp / aDn;
    if (ratio >= 1.5) return { cls: 'muy-favorable', label: 'Muy favorable' };
    if (ratio >= 1.0) return { cls: 'favorable', label: 'Favorable' };
    if (ratio >= 0.7) return { cls: 'equilibrado', label: 'Equilibrado' };
    return { cls: 'desfavorable', label: 'Desfavorable' };
  }

  function computeAsym(p) {
    var current = p.last_real_price || p.precio_actual;
    if (!current || !p.precio_base_6m) return null;
    var ref = p.precio_base_6m;
    if (p.validated_tesis === 'Optimista' && p.precio_optimista_6m) ref = p.precio_optimista_6m;
    else if (p.validated_tesis === 'Pesimista' && p.precio_pesimista_6m) ref = p.precio_pesimista_6m;
    var gap = (current - ref) * 100.0 / ref;
    if (gap <= -30) return { cls: 'infra-fuerte', label: 'Recorrido al alza fuerte' };
    if (gap <= -10) return { cls: 'infra-leve', label: 'Recorrido al alza leve' };
    if (gap < 10)   return { cls: 'neutra', label: 'Asimetría neutra' };
    if (gap < 30)   return { cls: 'sobre-leve', label: 'Recorrido a la baja leve' };
    return { cls: 'sobre-fuerte', label: 'Recorrido a la baja fuerte' };
  }

  function computeScenarioPrices(p) {
    var k = (p.validated_tesis || 'Base').toLowerCase();
    var key = k === 'pesimista' ? 'pesimista' : (k === 'optimista' ? 'optimista' : 'base');
    return {
      key: key,
      p6:  p['precio_' + key + '_6m']  || null,
      p12: p['precio_' + key + '_12m'] || null,
      p24: p['precio_' + key + '_24m'] || null
    };
  }

  function currentPriceOf(p) {
    return p.last_real_price || p.precio_actual || null;
  }

  function normalize(s) {
    return (s || '').toString().toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9 ]+/g, ' ')
      .replace(/\s+/g, ' ').trim();
  }

  function searchCoverage(query, limit) {
    if (!_coverage) return [];
    var q = normalize(query);
    if (!q || q.length < 1) return [];
    limit = limit || 8;
    var byTickerExact = [];
    var byTickerPrefix = [];
    var byNamePrefix = [];
    var byNameContains = [];
    for (var i = 0; i < _coverage.length; i++) {
      var c = _coverage[i];
      var t = normalize(c.ticker);
      var n = normalize(c.name);
      if (t === q) byTickerExact.push(c);
      else if (t.indexOf(q) === 0) byTickerPrefix.push(c);
      else if (n.indexOf(q) === 0) byNamePrefix.push(c);
      else if (n.indexOf(q) !== -1) byNameContains.push(c);
      if (byTickerExact.length + byTickerPrefix.length + byNamePrefix.length + byNameContains.length >= limit * 3) break;
    }
    return byTickerExact.concat(byTickerPrefix, byNamePrefix, byNameContains).slice(0, limit);
  }

  // ─── Relevancia (visión del prototipo) ───────────────────────────────────
  function relevance(cur, mag, base6m) {
    cur = isFinite(cur) ? cur : 0;
    mag = isFinite(mag) ? Math.abs(mag) : 0;
    base6m = isFinite(base6m) ? base6m : 0;
    var after = ((1 + cur / 100) * (1 - mag / 100) - 1) * 100;
    if (after >= 20) return { level: 'low', text: 'Ruido para tu posición' };
    if (after < 4)  return { level: 'high', text: 'Te afecta · vigilar' };
    return { level: 'mid', text: 'Impacto moderado' };
  }

  // ─── Snapshots de cartera para Make ─────────────────────────────────────
  // Make necesita la lista completa de tickers (positions) y solicitudes (requests)
  // para reconstruir el metafield del customer (no puede hacer read-merge por sí mismo).
  function buildPositionsSnapshot(positions) {
    return (positions || []).map(function (p) {
      return { ticker: p.ticker, product_gid: p.product_gid || '' };
    });
  }
  function buildRequestsSnapshot(requests) {
    return (requests || []).map(function (r) {
      return { ticker: r.ticker || '', name: r.name || '' };
    });
  }

  // ─── Sync con Make (3 webhooks separados por acción) ────────────────────
  /**
   * syncPortfolio({ action, ...payload })
   * Acciones contractuales: 'upsert_position', 'remove_position', 'request_coverage'
   * Cada acción usa su propio webhook de Make leído de `window.SR_PORTFOLIO_WEBHOOKS`.
   * Si la URL para la acción no está configurada → modo local (no envía red).
   * `keepalive:true` permite que el POST sobreviva a la redirección post-finalize.
   */
  function syncPortfolio(payload) {
    var s = loadState();
    s.pending_sync = true;
    saveState(s);
    var webhooks = window.SR_PORTFOLIO_WEBHOOKS || {};
    var url = ((webhooks[payload.action] || '') + '').trim();
    try { console.info('[syncPortfolio]', payload.action, url ? 'POST' : 'local', payload); } catch (_) {}
    if (!url) return Promise.resolve({ ok: true, stub: true });
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    }).then(function (r) { return { ok: r.ok, status: r.status }; })
      .catch(function () { return { ok: false }; });
  }

  // ─── Trial checkout: clear cart → add variant → begin_checkout → /checkout ─
  // Helper compartido entre onboarding (paso 3) y banner Mi cartera.
  // 1) Vacía el carrito para que el pedido sea solo de la prueba (sin items previos).
  // 2) Añade la variante de prueba (5€).
  // 3) Dispara el evento GA4 `begin_checkout` con item recomendado.
  // 4) Redirige a /checkout.
  // Devuelve Promise que solo rechaza si algo crítico falla; el caller decide fallback.
  function clearCartAddAndCheckout(variantId, extraData) {
    extraData = extraData || {};
    var vid = parseInt(variantId, 10);
    if (!vid) return Promise.reject(new Error('variant id invalid'));

    return fetch('/cart/clear.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
      .catch(function () { /* si el clear falla seguimos: el add suma; no romper UX */ })
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] })
        });
      })
      .then(function (r) {
        if (!r.ok) throw new Error('cart/add failed ' + r.status);
        return r.json();
      })
      .then(function () {
        // begin_checkout GA4 evento recomendado con shape estándar de ecommerce.
        var price = parseFloat(extraData.price) || 5.00;
        var beginPayload = {
          currency: extraData.currency || 'EUR',
          value: price,
          items: [{
            item_id: String(vid),
            item_name: extraData.item_name || 'Acceso Premium · Prueba 15 días',
            item_category: 'Suscripción · Prueba',
            price: price,
            quantity: 1
          }]
        };
        // Atributos custom (surface, positions_count, etc.) van fuera del shape ecommerce.
        if (extraData.surface) beginPayload.surface = extraData.surface;
        if (extraData.positions_count != null) beginPayload.positions_count = extraData.positions_count;
        track('begin_checkout', beginPayload);

        window.location.href = '/checkout';
      });
  }

  // ─── GA4 / dataLayer ─────────────────────────────────────────────────────
  function track(eventName, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, params || {}));
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params || {});
      }
    } catch (_) {}
  }

  // ─── Helpers de bloqueo premium ──────────────────────────────────────────
  // CTA inline para desbloquear. data-sr-unlock + data-unlock-location para tracking.
  function renderUnlockCTAHTML(location, label) {
    label = label || 'Regístrate gratis y accede a todo';
    return ''
      + '<button type="button" class="sr-unlock-inline" data-sr-unlock data-unlock-location="' + escapeHTML(location) + '" aria-label="' + escapeHTML(label) + '">'
      +   '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>'
      +   '<span>' + escapeHTML(label) + '</span>'
      + '</button>';
  }
  // Wrap blureado + overlay de bloqueo encima de contenido normalmente renderizado.
  function renderLockedWrapHTML(innerHTML, msg, location) {
    return ''
      + '<div class="sr-locked-wrap">'
      +   '<div class="sr-locked-content">' + innerHTML + '</div>'
      +   '<div class="sr-locked-overlay">'
      +     '<svg class="sr-locked-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>'
      +     (msg ? '<span class="sr-locked-msg">' + escapeHTML(msg) + '</span>' : '')
      +     renderUnlockCTAHTML(location)
      +   '</div>'
      + '</div>';
  }

  // ─── Render compartido de la card de posición (onboarding + mi-cartera) ─

  // Resuelve datos enriquecidos del coverage por ticker
  function findRich(ticker) {
    if (!_coverage) return null;
    for (var i = 0; i < _coverage.length; i++) {
      if (_coverage[i].ticker === ticker) return _coverage[i];
    }
    return null;
  }

  // Pill "Cumplimiento de tesis"
  function renderValidationSignalHTML(validation) {
    var v = (validation || 'Pendiente').toString();
    var cls = 'pendiente';
    if (/fuerte/i.test(v)) cls = 'fuerte';
    else if (/d[eé]bil/i.test(v)) cls = 'debil';
    return ''
      + '<div class="sr-pos__signal sr-signal sr-signal--validation-' + cls + '">'
      +   '<span class="sr-signal__dot"></span>'
      +   '<div class="sr-signal__txt">'
      +     '<span class="sr-signal__lbl">Cumplimiento de tesis</span>'
      +     '<span class="sr-signal__val">' + escapeHTML(v) + '</span>'
      +   '</div>'
      + '</div>';
  }

  // Señal "Tesis validada" (Optimista / Base / Pesimista) — el escenario que el modelo da por válido.
  function tesisScenarioCls(tesis) {
    var v = (tesis || 'Base').toString();
    if (/optimista/i.test(v)) return 'optimista';
    if (/pesimista/i.test(v)) return 'pesimista';
    if (/pendiente/i.test(v)) return 'pendiente';
    return 'base';
  }
  function renderTesisValidatedSignalHTML(tesis) {
    var v = (tesis || 'Base').toString();
    return ''
      + '<div class="sr-pos__signal sr-signal sr-signal--tesis-' + tesisScenarioCls(v) + '">'
      +   '<span class="sr-signal__dot"></span>'
      +   '<div class="sr-signal__txt">'
      +     '<span class="sr-signal__lbl">Tesis validada</span>'
      +     '<span class="sr-signal__val">' + escapeHTML(v) + '</span>'
      +   '</div>'
      + '</div>';
  }

  function formatReportDate(iso) {
    if (!iso || iso.length < 10) return iso || '';
    var p = iso.split('-');
    if (p.length !== 3) return iso;
    return p[2] + '/' + p[1] + '/' + p[0];
  }

  /**
   * Render compartido de una card de posición.
   * @param p   Objeto de la posición ({ ticker, name, net_shares, avg_price, invested, mode, transactions, product_gid, handle, ... })
   * @param idx Índice dentro de la lista
   * @param opts.ctx 'onboarding' (default) | 'mi-cartera'
   *   - 'onboarding': header con × para quitar (state local). Sin footer.
   *   - 'mi-cartera': header con × para quitar (Make sync). Footer con "Ver tesis →" y
   *     "+ Añadir compra/venta" (link a /pages/onboarding?ticker=X&action=add-tx).
   */
  function renderPositionCardHTML(p, idx, opts) {
    opts = opts || {};
    var ctx = opts.ctx || 'onboarding';
    // Gating premium: en mi-cartera, si NO es premium y NO es la primera card → bloquear secciones.
    var isPremium = !!opts.isPremium;
    var locked = (ctx === 'mi-cartera' && !isPremium && idx > 0);
    // En onboarding la fila muestra solo 3 columnas (Tu posición · Señales · Precio actual);
    // el bloque "Seguimiento de tesis" se reserva para la página Mi cartera.
    var showThesis = (ctx === 'mi-cartera');
    var rich = findRich(p.ticker);
    var isWatch = p.mode === 'watch';
    var currency = (rich && rich.currency) || '€';
    var currentPrice = rich ? currentPriceOf(rich) : null;
    var scen = rich ? computeScenarioPrices(rich) : null;
    var opp = rich ? computeOpp(rich) : null;
    var asym = rich ? computeAsym(rich) : null;
    var validatedTesis = rich ? (rich.validated_tesis || 'Base') : '';
    var tesisValidation = rich ? (rich.tesis_validation || 'Pendiente') : '';
    var reportDate = rich && rich.report_date ? formatReportDate(rich.report_date) : '';
    var productUrl = rich && rich.handle ? '/products/' + rich.handle : (p.handle ? '/products/' + p.handle : '#');

    function fmtPrice(n) {
      if (n == null) return '—';
      return currency + formatNumber(n, 2);
    }
    function fmtPct(price) {
      if (!price || !currentPrice) return '—';
      var pct = (price - currentPrice) * 100.0 / currentPrice;
      var sign = pct >= 0 ? '+' : '';
      return '<span class="sr-pos__delta sr-pos__delta--' + (pct >= 0 ? 'up' : 'down') + '">' + sign + pct.toFixed(1) + '%</span>';
    }

    // Bloque "Tu posición"
    var posDetail;
    if (isWatch) {
      posDetail = '<span class="sr-pos__mode-tag sr-pos__mode-tag--watch">Solo seguimiento</span>';
    } else {
      var sharesStr = formatNumber(p.net_shares, p.net_shares % 1 === 0 ? 0 : 4);
      var avg = p.avg_price || 0;
      var net = p.net_shares || 0;
      var invested = p.invested || 0;
      var realizedPL = p.realized_pl || 0;
      var hasRealized = Math.abs(realizedPL) > 0.005;
      var p6 = scen ? scen.p6 : null;
      var p12 = scen ? scen.p12 : null;
      var p24 = scen ? scen.p24 : null;

      function mineRow(horizon, price, isCurrent) {
        if (!price || !avg || net <= 0) {
          return ''
            + '<div class="sr-pos__mine-row">'
            +   '<span class="sr-pos__mine-rk">' + horizon + '</span>'
            +   '<span class="sr-pos__mine-rp">—</span>'
            +   '<span class="sr-pos__mine-rd">—</span>'
            + '</div>';
        }
        var portfolioValue = net * price;
        var latentPL = portfolioValue - invested;
        var totalPL = latentPL + realizedPL;
        var totalCost = invested;
        var pctOnCost = totalCost > 0 ? totalPL * 100 / totalCost : 0;
        var sign = totalPL >= 0 ? '+' : '';
        var pctSign = pctOnCost >= 0 ? '+' : '';
        var cls = totalPL >= 0 ? 'up' : 'down';
        return ''
          + '<div class="sr-pos__mine-row' + (isCurrent ? ' is-current' : '') + '">'
          +   '<span class="sr-pos__mine-rk">' + horizon + '</span>'
          +   '<span class="sr-pos__mine-rp">' + formatMoney(portfolioValue, currency) + '</span>'
          +   '<span class="sr-pos__mine-rd sr-pos__mine-rd--' + cls + '">' + sign + formatMoney(totalPL, currency) + ' · ' + pctSign + pctOnCost.toFixed(1) + '%</span>'
          + '</div>';
      }

      var summaryRows = ''
        + '<span><strong>' + escapeHTML(sharesStr) + '</strong> acc.</span>'
        + '<span>Medio <strong>' + fmtPrice(avg) + '</strong></span>'
        + '<span>Invertido <strong>' + formatMoney(invested, currency) + '</strong></span>';
      if (hasRealized) {
        var realizedSign = realizedPL >= 0 ? '+' : '';
        var realizedCls = realizedPL >= 0 ? 'up' : 'down';
        summaryRows += '<span class="sr-pos__mine-realized sr-pos__mine-rd--' + realizedCls + '">Ya realizado: ' + realizedSign + formatMoney(realizedPL, currency) + '</span>';
      }

      var projectionsHTML = mineRow('6 meses', p6) + mineRow('12 meses', p12) + mineRow('24 meses', p24);
      var projectionsBlock = locked
        ? renderLockedWrapHTML(projectionsHTML, 'Proyecciones 6m/12m/24m', 'projections')
        : projectionsHTML;
      posDetail = ''
        + '<div class="sr-pos__mine">'
        +   '<span class="sr-pos__mine-label">Tu posición</span>'
        +   '<div class="sr-pos__mine-summary">' + summaryRows + '</div>'
        +   '<div class="sr-pos__mine-table">'
        +     '<div class="sr-pos__mine-row sr-pos__mine-row--head">'
        +       '<span class="sr-pos__mine-rk">Plazo</span>'
        +       '<span class="sr-pos__mine-rp">Valor</span>'
        +       '<span class="sr-pos__mine-rd">G/P</span>'
        +     '</div>'
        +     mineRow('Actual', currentPrice, true)
        +     projectionsBlock
        +   '</div>'
        +   '<p class="sr-pos__mine-note">' + (hasRealized ? 'G/P incluye ' + (realizedPL >= 0 ? '+' : '') + formatMoney(realizedPL, currency) + ' ya realizados en ventas previas.' : 'G/P sobre el coste de adquisición (' + formatMoney(invested, currency) + ').') + '</p>'
        + '</div>';
    }

    // Atributos del data-position (necesarios para handlers de mi-cartera)
    var dataAttrs = ''
      + ' data-pos="' + idx + '"'
      + ' data-ticker="' + escapeHTML(p.ticker) + '"'
      + ' data-product-gid="' + escapeHTML(p.product_gid || '') + '"'
      + ' data-handle="' + escapeHTML(p.handle || (rich && rich.handle) || '') + '"'
      + ' data-net-shares="' + (p.net_shares || 0) + '"'
      + ' data-avg-price="' + (p.avg_price || 0) + '"'
      + ' data-invested="' + (p.invested || 0) + '"'
      + ' data-transactions="' + escapeHTML(JSON.stringify(p.transactions || [])) + '"';

    // Sin datos enriquecidos: render mínimo de "cargando"
    if (!rich) {
      return ''
        + '<article class="sr-pos sr-pos--loading"' + dataAttrs + '>'
        +   '<header class="sr-pos__head">'
        +     '<div class="sr-pos__head-id">'
        +       '<span class="sr-pill sr-pill--solid">' + escapeHTML(p.ticker) + '</span>'
        +       '<div>'
        +         '<h3 class="sr-pos__name">' + escapeHTML(p.name) + '</h3>'
        +         '<p class="sr-pos__sub">Cargando datos del modelo…</p>'
        +       '</div>'
        +     '</div>'
        +     '<button type="button" class="sr-pos__remove" data-pos-remove="' + idx + '" aria-label="Quitar ' + escapeHTML(p.ticker) + '">×</button>'
        +   '</header>'
        +   '<div class="sr-pos__body">' + posDetail + '</div>'
        + '</article>';
    }

    var tesisSnippet = locked
      ? firstWords(rich.tesis_summary, 12)
      : firstWords(rich.tesis_summary, 95);

    // Footer de acciones según contexto
    var followupUrl = (window.SR_LATEST_FOLLOWUPS || {})[p.ticker];
    var addTxUrl = '/pages/onboarding?ticker=' + encodeURIComponent(p.ticker) + '&action=add-tx';
    var footerHTML = '';
    if (ctx === 'mi-cartera') {
      if (locked) {
        // Free no-primera: ambos CTAs bloqueados, abren checkout
        footerHTML = ''
          + '<footer class="sr-pos__actions sr-pos__actions--left">'
          +   '<button type="button" class="sr-btn sr-btn--ghost sr-btn--small sr-btn--locked" data-sr-unlock data-unlock-location="footer-followup">'
          +     '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>'
          +     'Ver seguimiento'
          +   '</button>'
          +   '<button type="button" class="sr-btn sr-btn--ghost sr-btn--small sr-btn--locked" data-sr-unlock data-unlock-location="footer-thesis">'
          +     '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>'
          +     'Ver tesis'
          +   '</button>'
          + '</footer>';
      } else {
        // Premium o primera card free: links normales, ambos a la izquierda
        footerHTML = ''
          + '<footer class="sr-pos__actions sr-pos__actions--left">'
          + (followupUrl
              ? '<a class="sr-btn sr-btn--primary sr-btn--small" href="' + followupUrl + '">Ver seguimiento →</a>'
              : '')
          +   '<a class="sr-btn sr-btn--ghost sr-btn--small" href="' + productUrl + '">Ver tesis →</a>'
          + '</footer>';
      }
    }

    // Acciones top-right de la card.
    // En mi-cartera: icono lápiz para añadir tx + × para quitar.
    // En onboarding: solo ×.
    var headerActions;
    if (ctx === 'mi-cartera') {
      headerActions = ''
        + '<div class="sr-pos__head-actions">'
        +   '<a class="sr-pos__icon-btn sr-pos__icon-btn--edit" href="' + addTxUrl + '"'
        +     ' title="Añadir compra o venta"'
        +     ' aria-label="Añadir compra o venta a ' + escapeHTML(p.ticker) + '">'
        +     '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>'
        +   '</a>'
        +   '<button type="button" class="sr-pos__remove" data-remove-position'
        +     ' data-ticker="' + escapeHTML(p.ticker) + '"'
        +     ' data-product-gid="' + escapeHTML(p.product_gid || '') + '"'
        +     ' title="Quitar de la cartera"'
        +     ' aria-label="Quitar ' + escapeHTML(p.ticker) + ' de tu cartera">×</button>'
        + '</div>';
    } else {
      headerActions = '<button type="button" class="sr-pos__remove" data-pos-remove="' + idx + '" aria-label="Quitar ' + escapeHTML(p.ticker) + '">×</button>';
    }

    // Caja Precio actual: gráfica de 3 escenarios (reutiliza InformeChart / Chart.js,
    // la misma de la card de producto informe). Los datos ya vienen del coverage (rich).
    var scenarioChartHTML = ''
      + '<div class="sr-pos__chart" data-report-chart-js'
      +   ' data-ticker="' + escapeHTML(p.ticker) + '"'
      +   ' data-currency="' + escapeHTML(currency) + '"'
      +   ' data-actual="' + (currentPrice || 0) + '"'
      +   ' data-report-date="' + escapeHTML(rich.report_date || '') + '"'
      +   ' data-6m="' + (rich.precio_base_6m || 0) + '"'
      +   ' data-12m="' + (rich.precio_base_12m || 0) + '"'
      +   ' data-24m="' + (rich.precio_base_24m || 0) + '"'
      +   ' data-bear-6m="' + (rich.precio_pesimista_6m || 0) + '"'
      +   ' data-bear-12m="' + (rich.precio_pesimista_12m || 0) + '"'
      +   ' data-bear-24m="' + (rich.precio_pesimista_24m || 0) + '"'
      +   ' data-bull-6m="' + (rich.precio_optimista_6m || 0) + '"'
      +   ' data-bull-12m="' + (rich.precio_optimista_12m || 0) + '"'
      +   ' data-bull-24m="' + (rich.precio_optimista_24m || 0) + '"'
      +   ' data-validated-tesis="' + escapeHTML(validatedTesis) + '">'
      +   '<canvas class="report-card-chart sr-pos__chart-canvas"></canvas>'
      + '</div>';

    return ''
      + '<article class="sr-pos' + (isWatch ? ' sr-pos--watch' : '') + '"' + dataAttrs + '>'
      +   '<header class="sr-pos__head">'
      +     '<div class="sr-pos__head-id">'
      +       '<span class="sr-pill sr-pill--solid">' + escapeHTML(p.ticker) + '</span>'
      +       '<div class="sr-pos__title-wrap">'
      +         '<h3 class="sr-pos__name">' + escapeHTML(p.name) + '</h3>'
      +         '<p class="sr-pos__sub">'
      +           (rich.sector ? escapeHTML(rich.sector) : '')
      +           (rich.country ? ' · ' + escapeHTML(rich.country) : '')
      +           (reportDate ? ' · Última validación ' + escapeHTML(reportDate) : '')
      +         '</p>'
      +       '</div>'
      +     '</div>'
      +     headerActions
      +   '</header>'

      +   '<div class="sr-pos__body">'
      +     '<div class="sr-pos__grid ' + (showThesis ? 'sr-pos__grid--4col' : 'sr-pos__grid--3col') + '">'

      +       '<div class="sr-pos__block sr-pos__block--mine">'
      +         posDetail
      +       '</div>'

      +       '<div class="sr-pos__block sr-pos__block--signals">'
      +         '<span class="sr-pos__block-label">Señales del modelo</span>'
      + (locked
          ? renderLockedWrapHTML(
              renderValidationSignalHTML(tesisValidation)
              + renderTesisValidatedSignalHTML(validatedTesis)
              + (opp ? '<div class="sr-pos__signal sr-signal sr-signal--opp-' + opp.cls + '"><span class="sr-signal__dot"></span><div class="sr-signal__txt"><span class="sr-signal__lbl">Oportunidad vs Riesgo</span><span class="sr-signal__val">' + opp.label + '</span></div></div>' : '')
              + (asym ? '<div class="sr-pos__signal sr-signal sr-signal--asym-' + asym.cls + '"><span class="sr-signal__dot"></span><div class="sr-signal__txt"><span class="sr-signal__lbl">Asimetría</span><span class="sr-signal__val">' + asym.label + '</span></div></div>' : ''),
              'Cumplimiento, tesis, oportunidad y asimetría',
              'signals'
            )
          : renderValidationSignalHTML(tesisValidation)
            + renderTesisValidatedSignalHTML(validatedTesis)
            + (opp ? '<div class="sr-pos__signal sr-signal sr-signal--opp-' + opp.cls + '"><span class="sr-signal__dot"></span><div class="sr-signal__txt"><span class="sr-signal__lbl">Oportunidad vs Riesgo</span><span class="sr-signal__val">' + opp.label + '</span></div></div>' : '<div class="sr-pos__signal sr-signal sr-signal--missing">Sin datos suficientes</div>')
            + (asym ? '<div class="sr-pos__signal sr-signal sr-signal--asym-' + asym.cls + '"><span class="sr-signal__dot"></span><div class="sr-signal__txt"><span class="sr-signal__lbl">Asimetría</span><span class="sr-signal__val">' + asym.label + '</span></div></div>' : ''))
      +       '</div>'

      + (showThesis
          ? '<div class="sr-pos__block sr-pos__block--thesis' + (locked ? ' sr-pos__block--thesis-locked' : '') + '">'
            +   '<span class="sr-pos__block-label">Seguimiento de tesis</span>'
            + (tesisSnippet
                ? '<p class="sr-pos__thesis-text">' + escapeHTML(tesisSnippet) + (locked ? '… <span class="sr-pos__thesis-fade"></span>' : '') + '</p>'
                : '<p class="sr-pos__thesis-text sr-pos__thesis-text--empty">Seguimiento en preparación.</p>')
            + (locked
                ? renderUnlockCTAHTML('thesis-summary', 'Regístrate gratis para ver el seguimiento completo')
                : (followupUrl
                    ? '<a class="sr-pos__thesis-link" href="' + followupUrl + '">Ver seguimiento →</a>'
                    : (productUrl && productUrl !== '#' ? '<a class="sr-pos__thesis-link" href="' + productUrl + '">Ver tesis →</a>' : '')))
            + '</div>'
          : '')

      +       '<div class="sr-pos__block sr-pos__block--price">'
      +         '<span class="sr-pos__block-label">Precio actual</span>'
      +         '<div class="sr-pos__price-head">'
      +           '<span class="sr-pos__price-now">' + fmtPrice(currentPrice) + '</span>'
      +           '<span class="sr-pos__scenario-pill sr-pos__scenario-pill--' + tesisScenarioCls(validatedTesis) + '">Escenario ' + escapeHTML(validatedTesis) + '</span>'
      +         '</div>'
      + (locked
          ? renderLockedWrapHTML(scenarioChartHTML, 'Escenarios 6m/12m/24m · regístrate gratis', 'scenarios')
          : scenarioChartHTML)
      +       '</div>'

      +     '</div>'
      +   '</div>'
      +   footerHTML
      + '</article>';
  }

  // Dibuja las gráficas de escenarios de las cards ya insertadas en `container`.
  // Reutiliza window.InformeChart (Chart.js), igual que las cards del ranking.
  function renderPositionCharts(container) {
    if (!container) return;
    (function tryRender() {
      if (typeof Chart === 'undefined' || !window.InformeChart) {
        setTimeout(tryRender, 120);
        return;
      }
      container.querySelectorAll('[data-report-chart-js]').forEach(function (el) {
        var canvas = el.querySelector('.report-card-chart');
        if (!canvas) return;
        // Re-render idempotente: destruye la instancia previa (para enriquecer con
        // las series cuando llegan de la consulta dirigida).
        if (el._srChart) { try { el._srChart.destroy(); } catch (_) {} el._srChart = null; }
        var ticker = el.getAttribute('data-ticker') || '';
        var series = POSITION_SERIES[ticker] || {};
        el._srChart = window.InformeChart.render(canvas, {
          currency: el.dataset.currency || '',
          actual: parseFloat(el.dataset.actual) || 0,
          reportDate: el.dataset.reportDate || '',
          base: { m6: parseFloat(el.dataset['6m']) || 0, m12: parseFloat(el.dataset['12m']) || 0, m24: parseFloat(el.dataset['24m']) || 0 },
          bear: { m6: parseFloat(el.getAttribute('data-bear-6m')) || 0, m12: parseFloat(el.getAttribute('data-bear-12m')) || 0, m24: parseFloat(el.getAttribute('data-bear-24m')) || 0 },
          bull: { m6: parseFloat(el.getAttribute('data-bull-6m')) || 0, m12: parseFloat(el.getAttribute('data-bull-12m')) || 0, m24: parseFloat(el.getAttribute('data-bull-24m')) || 0 },
          evolution: series.evolution || [],
          analystTargets: series.analystTargets || [],
          validatedTesis: el.getAttribute('data-validated-tesis') || 'Base',
          showLegend: false,
          compact: true
        });
      });
    })();
  }

  // ─── Onboarding UI ───────────────────────────────────────────────────────
  function bootOnboarding(root) {
    var customerId = root.getAttribute('data-customer-id') || '';

    // 3 webhooks de Make por acción (R1/R3). Fallback retrocompat al data-webhook-url legacy = upsert.
    var legacyWebhook = (root.getAttribute('data-webhook-url') || '').trim();
    window.SR_PORTFOLIO_WEBHOOKS = {
      upsert_position:  (root.getAttribute('data-webhook-upsert')  || legacyWebhook || '').trim(),
      remove_position:  (root.getAttribute('data-webhook-remove')  || '').trim(),
      request_coverage: (root.getAttribute('data-webhook-request') || '').trim()
    };

    var maxPositions = parseInt(root.getAttribute('data-max-positions') || '10', 10);

    var searchInput = root.querySelector('[data-sr-search]');
    var resultsEl  = root.querySelector('[data-sr-results]');
    var chipsEl    = root.querySelector('[data-sr-chips]');
    var counterEl  = root.querySelector('[data-sr-counter]');
    var ledgerWrap = root.querySelector('[data-sr-ledger]');
    var coverageWrap = root.querySelector('[data-sr-coverage-request]');
    var confirmCta = root.querySelector('[data-sr-confirm]');
    var registerStep = root.querySelector('[data-sr-register-step]');
    var stepperEl  = root.querySelector('[data-sr-stepper]');
    var maxWarning = root.querySelector('[data-sr-max-warning]');
    var nextBtn    = root.querySelector('[data-sr-next="1"]');
    var nextHint   = root.querySelector('[data-sr-next-hint]');
    var summaryListEl  = root.querySelector('[data-sr-summary-list]');
    var summaryCountEl = root.querySelector('[data-sr-summary-count]');
    var summaryEmptyEl = root.querySelector('[data-sr-summary-empty]');
    var emptyAlertEl   = root.querySelector('[data-sr-empty]');
    var panels     = root.querySelectorAll('[data-sr-panel]');
    var maxStepVisited = 1;
    var currentStep = 1;

    var state = loadState();
    var registerStepShown = false;

    // ¿El usuario acaba de crear la cuenta dentro de este flujo? (flag puesto antes del submit
    // nativo del registro). Se lee aquí sin consumirlo; el bloque account_created lo elimina luego.
    var justRegistered = false;
    try { justRegistered = sessionStorage.getItem('sr_register_pending') === '1'; } catch (_) {}

    // Server siempre wins (cuando hay sesión): si el customer tiene cartera en Shopify,
    // sustituimos el localStorage por los datos del server. Evita que el snapshot enviado
    // a Make en finalize "pierda" posiciones ya guardadas.
    if (customerId && Array.isArray(window.SR_SERVER_PORTFOLIO)) {
      var serverPositions = window.SR_SERVER_PORTFOLIO || [];
      var serverRequests  = window.SR_SERVER_COVERAGE_REQUESTS || [];
      // Solo reescribimos si el server tiene algo, o si el localStorage está vacío
      // (evita pisar localStorage en pleno onboarding nuevo del mismo usuario).
      if (serverPositions.length > 0 || serverRequests.length > 0 || state.positions.length === 0) {
        state.positions = serverPositions.slice();
        state.coverage_requests = serverRequests.slice();
        state.pending_sync = false;
        saveState(state);
        try { console.info('[bootOnboarding] hydrated from server:', state.positions.length, 'positions,', state.coverage_requests.length, 'requests'); } catch (_) {}
      }
    }

    // R-track · onboarding_view
    track('onboarding_view', {
      logged_in: !!customerId,
      positions_count: state.positions.length
    });

    // Loading bloqueante mientras llegan los datos del Storefront API
    showLoadingOverlay();
    loadCoverage().then(function () {
      hideLoadingOverlay();
      renderState();
      // Deep-link: si llegamos con ?ticker=X[&action=add-tx], abrir el ledger sobre esa posición.
      try {
        var urlParams = new URL(window.location.href).searchParams;
        var deepTicker = (urlParams.get('ticker') || '').toUpperCase();
        var deepAction = urlParams.get('action') || '';
        if (deepTicker && currentStep === 1) {
          var hit = (_coverage || []).find(function (c) { return c.ticker === deepTicker; });
          if (hit) {
            // pickCovered detecta automáticamente si existe en state.positions → modo edición.
            pickCovered(hit);
            // Limpiar el query para no re-disparar en próximas cargas (sin recargar).
            urlParams.delete('ticker');
            urlParams.delete('action');
            var u = new URL(window.location.href);
            u.search = urlParams.toString();
            window.history.replaceState({}, '', u.toString());
          }
        }
      } catch (_) {}

      // Deep-link: ?add={handle} (desde "Añadir a cartera" anónimo, Fase 4) o
      // ?perla={handle} (llegada desde la perla: popup/landing → onboarding) →
      // sembrar la empresa como posición de solo seguimiento (watch) en el paso 1.
      try {
        var addParams = new URL(window.location.href).searchParams;
        var addHandle = (addParams.get('add') || addParams.get('perla') || '').trim().toLowerCase();
        if (addHandle && currentStep === 1) {
          var addHit = (_coverage || []).find(function (c) {
            return (c.handle || '').toLowerCase() === addHandle;
          });
          if (addHit) {
            var already = state.positions.some(function (p) { return p.ticker === addHit.ticker; });
            if (!already) {
              state.positions.push({
                ticker: addHit.ticker,
                handle: addHit.handle,
                product_gid: addHit.product_gid,
                name: addHit.name,
                mode: 'watch',
                transactions: [],
                net_shares: 0,
                avg_price: 0,
                invested: 0,
                updated_at: new Date().toISOString()
              });
              saveState(state);
              renderState();
              track('add_to_portfolio', { ticker: addHit.ticker, mode: 'watch', source: 'onboarding-seed' });
            }
          }
          // Limpiar ?add/?perla para no re-sembrar en recargas (conservamos ?from para analítica).
          addParams.delete('add');
          addParams.delete('perla');
          var u2 = new URL(window.location.href);
          u2.search = addParams.toString();
          window.history.replaceState({}, '', u2.toString());
        }
      } catch (_) {}
    });

    function showLoadingOverlay() {
      var panel = root.querySelector('[data-sr-panel="1"]');
      if (!panel) return;
      if (panel.querySelector('[data-sr-loading]')) return;
      var ov = document.createElement('div');
      ov.className = 'sr-loading';
      ov.setAttribute('data-sr-loading', '');
      ov.setAttribute('role', 'status');
      ov.setAttribute('aria-live', 'polite');
      ov.innerHTML = ''
        + '<div class="sr-loading__inner">'
        +   '<div class="sr-loading__spinner" aria-hidden="true"></div>'
        +   '<p class="sr-loading__text">Cargando datos del modelo…</p>'
        +   '<p class="sr-loading__sub">Estamos preparando los escenarios, oportunidad y asimetría de las 277 empresas en seguimiento.</p>'
        + '</div>';
      panel.appendChild(ov);
      panel.classList.add('sr-step-panel--loading');
    }
    function hideLoadingOverlay() {
      var ov = root.querySelector('[data-sr-loading]');
      if (ov && ov.parentNode) ov.parentNode.removeChild(ov);
      root.querySelectorAll('.sr-step-panel--loading').forEach(function (el) { el.classList.remove('sr-step-panel--loading'); });
    }

    // ── Wizard navigation
    function setStep(n) {
      // Si llegamos al 2 pero ya hay sesión, saltar al 3
      if (n === 2 && customerId) n = 3;
      // Salvaguarda: no permitir saltar al 3 sin posición o (si anónimo) sin paso 2 completado
      if (n === 3 && state.positions.length === 0 && state.coverage_requests.length === 0) n = 1;
      currentStep = n;
      if (n > maxStepVisited) maxStepVisited = n;

      panels.forEach(function (p) {
        var pn = parseInt(p.getAttribute('data-sr-panel'), 10);
        var on = pn === n;
        p.hidden = !on;
        p.classList.toggle('is-current', on);
      });

      updateStepper();

      // Scroll al top del panel (UX wizard)
      try {
        var panel = root.querySelector('[data-sr-panel="' + n + '"]');
        if (panel) panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (_) {}

      // Actualizar URL sin recargar
      try {
        var u = new URL(window.location.href);
        if (n === 1) u.searchParams.delete('step');
        else u.searchParams.set('step', String(n));
        window.history.replaceState({}, '', u.toString());
      } catch (_) {}

      // Render del paso 3 si toca
      if (n === 3) {
        renderSummary();
        if (trialEl) {
          renderTrialPersonalization();
          if (!trialOfferViewedFired) {
            trialOfferViewedFired = true;
            var variantId = trialEl.getAttribute('data-trial-variant-id') || '';
            track('trial_offer_viewed', { variant: variantId, positions_count: state.positions.length });
          }
        }
      }

      // R-track register_step_viewed
      if (n === 2 && !registerStepShown) {
        registerStepShown = true;
        track('register_step_viewed', { positions_count: state.positions.length, requests_count: state.coverage_requests.length });
      }
    }

    // Configura el stepper según el estado de sesión (3 modos):
    //  - anónimo: 3 pasos normales.
    //  - logueado recién registrado: 3 pasos, paso 2 "Crea tu cuenta" hecho y no clicable.
    //  - logueado gestionando (sesión previa): 2 pasos, se oculta el paso 2 y se renumera Confirma a "2".
    function configureStepper() {
      if (!stepperEl) return;
      var step2 = stepperEl.querySelector('[data-step-account]');
      var confirmNum = stepperEl.querySelector('[data-step-confirm-num]');
      if (!customerId) {
        if (step2) { step2.hidden = false; step2.classList.remove('is-disabled'); }
        if (confirmNum) confirmNum.textContent = '3';
      } else if (justRegistered) {
        if (step2) {
          step2.hidden = false;
          step2.classList.add('is-done', 'is-disabled');
          step2.removeAttribute('data-step-clickable');
        }
        if (confirmNum) confirmNum.textContent = '3';
      } else {
        if (step2) step2.hidden = true;
        if (confirmNum) confirmNum.textContent = '2';
      }
    }

    function updateStepper() {
      if (!stepperEl) return;
      var steps = stepperEl.querySelectorAll('[data-step]');
      steps.forEach(function (s) {
        var sn = parseInt(s.getAttribute('data-step'), 10);
        var disabled = s.classList.contains('is-disabled');
        s.classList.remove('is-active', 'is-clickable');
        if (!disabled) s.classList.remove('is-done');
        if (sn === currentStep) {
          s.classList.add('is-active');
        } else if (sn < currentStep) {
          s.classList.add('is-done');
        }
        // Clickable solo hacia atrás (pasos ya visitados, anteriores al actual y no deshabilitados)
        if (sn < currentStep && sn <= maxStepVisited && !disabled) {
          s.classList.add('is-clickable');
        }
      });
    }

    // Click en stepper → navegar atrás
    if (stepperEl) {
      stepperEl.addEventListener('click', function (e) {
        var step = e.target.closest('[data-step]');
        if (!step) return;
        if (!step.classList.contains('is-clickable')) return;
        var n = parseInt(step.getAttribute('data-step'), 10);
        if (n && n < currentStep) setStep(n);
      });
    }

    // Botón "Continuar" del paso 1
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        if (nextBtn.disabled) return;
        setStep(2); // setStep saltará a 3 si ya hay sesión
      });
    }

    // Botones "← Volver"
    root.querySelectorAll('[data-sr-back]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var n = parseInt(btn.getAttribute('data-sr-back'), 10);
        if (n) setStep(n);
      });
    });

    // Estado inicial del paso. Solo saltamos a un step distinto del 1 si el query string
    // lo indica explícitamente (?step=N o ?finalize=1 legacy).
    // El caso "logueado con posiciones locales" ya NO fuerza step 3, porque al volver
    // desde Mi cartera con datos hidratados del server el user quiere editar (step 1).
    configureStepper();

    // Belt-and-suspenders (el liquid ya lo gatea server-side): si el usuario ya tiene cartera
    // guardada, ocultar el grid de descubrimiento del paso 3.
    try {
      if (Array.isArray(window.SR_SERVER_PORTFOLIO) && window.SR_SERVER_PORTFOLIO.length > 0) {
        var __su = root.querySelector('[data-sr-suite-unlock]');
        if (__su) __su.style.display = 'none';
      }
    } catch (_) {}

    var initialStep = 1;
    try {
      var u0 = new URL(window.location.href);
      var stepParam = u0.searchParams.get('step');
      var finalizeParam = u0.searchParams.get('finalize') === '1';
      if (finalizeParam) {
        // Legacy: ?finalize=1 (link viejo) — tratar como step=3
        if (customerId) initialStep = 3;
      } else if (stepParam === '2') {
        initialStep = customerId ? 3 : 2;
      } else if (stepParam === '3') {
        initialStep = customerId ? 3 : 1;
      }
    } catch (_) {}
    if (initialStep > 1) setStep(initialStep);
    else updateStepper();

    // R12 · Autofocus en buscador (solo si el viewport está cerca del top y no es móvil con scroll)
    if (searchInput) {
      var __rect = searchInput.getBoundingClientRect();
      var nearTop = __rect.top >= 0 && __rect.top < window.innerHeight * 0.8;
      if (nearTop) {
        // En móvil, evitar levantar el teclado automáticamente (UX desagradable)
        var isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
        if (!isMobile) {
          try { searchInput.focus({ preventScroll: true }); } catch (_) { searchInput.focus(); }
        }
      }
    }

    // ── Search
    var debounce = null;
    var currentHits = [];
    var activeHitIdx = -1;
    var searchEventSent = false;
    if (searchInput) {
      searchInput.addEventListener('input', function () {
        clearTimeout(debounce);
        var q = searchInput.value;
        debounce = setTimeout(function () { runSearch(q); }, 120);
      });
      // R11 · navegación por teclado en resultados
      searchInput.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { hideResults(); return; }
        if (resultsEl && resultsEl.style.display !== 'none' && currentHits.length > 0) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            activeHitIdx = (activeHitIdx + 1) % currentHits.length;
            updateActiveHit();
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            activeHitIdx = activeHitIdx <= 0 ? currentHits.length - 1 : activeHitIdx - 1;
            updateActiveHit();
          } else if (e.key === 'Enter' && activeHitIdx >= 0) {
            e.preventDefault();
            pickCovered(currentHits[activeHitIdx]);
          }
        }
      });
    }

    function updateActiveHit() {
      if (!resultsEl) return;
      resultsEl.querySelectorAll('.sr-hit').forEach(function (row, i) {
        var on = i === activeHitIdx;
        row.setAttribute('aria-selected', on ? 'true' : 'false');
        row.classList.toggle('is-active', on);
        if (on) row.scrollIntoView({ block: 'nearest' });
      });
      if (activeHitIdx >= 0) {
        searchInput.setAttribute('aria-activedescendant', 'sr-hit-' + activeHitIdx);
      }
    }

    function runSearch(q) {
      if (!resultsEl) return;
      var trimmed = (q || '').trim();
      if (!trimmed) { hideResults(); currentHits = []; activeHitIdx = -1; return; }
      var hits = searchCoverage(trimmed, 8);
      currentHits = hits;
      activeHitIdx = -1;
      if (!searchEventSent && trimmed.length >= 2) {
        searchEventSent = true;
        track('search_performed', { query_length: trimmed.length });
      }
      if (hits.length > 0) {
        resultsEl.innerHTML = hits.map(renderHitHTML).join('');
        resultsEl.style.display = '';
        // CTA "Añadir a cartera" → abre el ledger (decisión de modo primero).
        resultsEl.querySelectorAll('[data-pick]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var idx = parseInt(btn.getAttribute('data-pick'), 10);
            pickCovered(hits[idx]);
          });
        });
        // Hover/foco sobre la fila → resaltar (navegación por teclado coherente).
        resultsEl.querySelectorAll('.sr-hit').forEach(function (row) {
          row.addEventListener('mouseenter', function () {
            activeHitIdx = parseInt(row.getAttribute('data-hit'), 10);
            updateActiveHit();
          });
        });
        if (coverageWrap) coverageWrap.style.display = 'none';
      } else {
        resultsEl.innerHTML = renderNoMatchHTML(trimmed);
        resultsEl.style.display = '';
        var openBtn = resultsEl.querySelector('[data-open-coverage]');
        if (openBtn) openBtn.addEventListener('click', function () { openCoverageRequest(trimmed); });
      }
    }

    function renderHitHTML(c, i) {
      var sector = c.sector ? '<span class="sr-pill">' + escapeHTML(c.sector) + '</span>' : '';
      var country = c.country ? '<span class="sr-pill sr-pill--soft">' + escapeHTML(c.country) + '</span>' : '';
      return ''
        + '<div class="sr-hit" id="sr-hit-' + i + '" data-hit="' + i + '" role="option" aria-selected="false">'
        +   '<div class="sr-hit__main">'
        +     '<span class="sr-hit__ticker">' + escapeHTML(c.ticker) + '</span>'
        +     '<span class="sr-hit__name">' + escapeHTML(c.name) + '</span>'
        +   '</div>'
        +   '<div class="sr-hit__meta">' + sector + country + '</div>'
        +   '<span class="sr-hit__badge sr-hit__badge--free">En seguimiento</span>'
        +   '<button type="button" class="sr-hit__add" data-pick="' + i + '" aria-label="Añadir ' + escapeHTML(c.ticker) + ' a mi cartera">'
        +     '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>'
        +     '<span>Añadir a cartera</span>'
        +   '</button>'
        + '</div>';
    }

    function renderNoMatchHTML(q) {
      return ''
        + '<div class="sr-nomatch">'
        +   '<div class="sr-nomatch__title">«' + escapeHTML(q) + '» aún no está en seguimiento</div>'
        +   '<div class="sr-nomatch__meta">Bajo solicitud de cobertura</div>'
        +   '<button type="button" class="sr-btn sr-btn--ghost" data-open-coverage>Solicitar cobertura</button>'
        + '</div>';
    }

    function hideResults() {
      if (resultsEl) { resultsEl.style.display = 'none'; resultsEl.innerHTML = ''; }
    }

    // ── Pick empresa cubierta → abrir libro de movimientos
    var activePick = null;
    function pickCovered(c) {
      activePick = c;
      track('company_picked', { ticker: c.ticker, sector: c.sector || '' });
      if (!ledgerWrap) return;
      // Detectar si la empresa ya está en cartera → modo edición sobre la posición existente.
      var existingIdx = state.positions.findIndex(function (p) { return p.ticker === c.ticker; });
      var editingPos = existingIdx >= 0 ? state.positions[existingIdx] : null;
      var opts = editingPos
        ? { editing: true, editingIdx: existingIdx, existingPos: editingPos }
        : { editing: false };
      ledgerWrap.innerHTML = renderLedgerHTML(c, opts);
      ledgerWrap.style.display = '';
      hideResults();
      bindLedger(ledgerWrap, opts);
      ledgerWrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function renderLedgerHTML(c, opts) {
      opts = opts || {};
      var editing = !!opts.editing;
      var existingPos = opts.existingPos || null;
      // Alta nueva → "Solo seguimiento" por defecto (cero fricción). En edición se respeta el modo guardado.
      var initialMode = (editing && existingPos)
        ? (existingPos.mode === 'watch' ? 'watch' : 'positions')
        : 'watch';
      var titleSuffix = editing ? ' <span class="sr-ledger__edit-tag">Editando</span>' : '';

      // En edición, prellenamos las filas con las transacciones existentes.
      var rowsHTML = '';
      if (editing && existingPos && Array.isArray(existingPos.transactions) && existingPos.transactions.length > 0) {
        rowsHTML = existingPos.transactions.map(function (tx, i) { return renderLedgerRowHTML(i, tx); }).join('');
      } else {
        rowsHTML = renderLedgerRowHTML(0);
      }

      return ''
        + '<div class="sr-ledger' + (editing ? ' sr-ledger--editing' : '') + '" data-ticker="' + escapeHTML(c.ticker) + '" data-product-gid="' + escapeHTML(c.product_gid || '') + '" data-handle="' + escapeHTML(c.handle || '') + '">'
        +   '<div class="sr-ledger__head">'
        +     '<div class="sr-ledger__title"><span class="sr-pill sr-pill--solid">' + escapeHTML(c.ticker) + '</span> <span class="sr-ledger__name">' + escapeHTML(c.name) + '</span>' + titleSuffix + '</div>'
        +     '<button type="button" class="sr-icon-btn" data-ledger-cancel title="Cancelar" aria-label="Cancelar y volver">×</button>'
        +   '</div>'

        +   '<div class="sr-ledger__section">'
        +     '<span class="sr-ledger__step-label">Paso A · ¿Cómo quieres seguirla?</span>'
        +     '<div class="sr-ledger__mode" role="group" aria-label="Modo de seguimiento">'
        +       '<label class="sr-mode__opt' + (initialMode === 'watch' ? ' is-active' : '') + '" data-mode-opt="watch">'
        +         '<input type="radio" name="sr-mode-' + escapeHTML(c.ticker) + '" value="watch"' + (initialMode === 'watch' ? ' checked' : '') + '>'
        +         '<span class="sr-mode__title">Solo seguimiento</span>'
        +         '<span class="sr-mode__desc">Sin importes ahora. Recibes los avisos igual; añades los números cuando quieras.</span>'
        +       '</label>'
        +       '<label class="sr-mode__opt' + (initialMode === 'positions' ? ' is-active' : '') + '" data-mode-opt="positions">'
        +         '<input type="radio" name="sr-mode-' + escapeHTML(c.ticker) + '" value="positions"' + (initialMode === 'positions' ? ' checked' : '') + '>'
        +         '<span class="sr-mode__title">Con importes</span>'
        +         '<span class="sr-mode__desc">Verás tu rentabilidad real y la relevancia de los avisos según tu precio medio.</span>'
        +       '</label>'
        +     '</div>'
        +   '</div>'

        +   '<div class="sr-ledger__movements" data-ledger-movements>'
        +     '<span class="sr-ledger__step-label">Paso B · Tus movimientos</span>'
        +     '<p class="sr-ledger__movements-intro">Añade tus compras y ventas. Calculamos el precio medio y el invertido en vivo; puedes editarlos cuando quieras desde Mi cartera.</p>'
        +     '<div class="sr-ledger__rows" data-ledger-rows>' + rowsHTML + '</div>'
        +     '<button type="button" class="sr-btn sr-btn--add-row" data-ledger-add>'
        +       '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>'
        +       'Añadir otra compra o venta'
        +     '</button>'
        +     '<div class="sr-ledger__summary" data-ledger-summary>'
        +       '<div><span class="sr-ledger__sk">Posición neta</span><span class="sr-ledger__sv" data-sum-net>0</span></div>'
        +       '<div><span class="sr-ledger__sk">Precio medio</span><span class="sr-ledger__sv" data-sum-avg>—</span></div>'
        +       '<div><span class="sr-ledger__sk">Invertido</span><span class="sr-ledger__sv" data-sum-invested>—</span></div>'
        +     '</div>'
        +   '</div>'

        +   '<div class="sr-ledger__error" data-ledger-error style="display:none" role="alert"></div>'
        +   '<div class="sr-ledger__actions">'
        +     '<button type="button" class="sr-btn sr-btn--primary" data-ledger-save data-mode="' + initialMode + '">Añadir a mi cartera</button>'
        +   '</div>'
        + '</div>';
    }

    function renderLedgerRowHTML(idx, tx) {
      tx = tx || {};
      var type = tx.type === 'sell' ? 'sell' : 'buy';
      var date = tx.date || '';
      var amount = (tx.amount != null && tx.amount !== 0) ? tx.amount : '';
      var shares = (tx.shares != null && tx.shares !== 0) ? tx.shares : '';
      var sym = (activePick && activePick.currency) || '€';
      return ''
        + '<div class="sr-ledger-row" data-row>'
        +   '<label class="sr-field"><span class="sr-field__label">Movimiento</span>'
        +     '<select data-row-type>'
        +       '<option value="buy"' + (type === 'buy' ? ' selected' : '') + '>Compra</option>'
        +       '<option value="sell"' + (type === 'sell' ? ' selected' : '') + '>Venta</option>'
        +     '</select>'
        +   '</label>'
        +   '<label class="sr-field"><span class="sr-field__label">Fecha</span>'
        +     '<input type="date" data-row-date value="' + escapeHTML(String(date)) + '" />'
        +   '</label>'
        +   '<label class="sr-field"><span class="sr-field__label">Importe total (' + escapeHTML(sym) + ')'
        +       '<span class="sr-tip" tabindex="0" role="button" aria-label="¿Qué importe pongo?" data-tip="Lo que pagaste en total por esta operación, en la moneda de la empresa (' + escapeHTML(sym) + '), comisiones aparte.">?</span>'
        +     '</span>'
        +     '<input type="text" inputmode="decimal" data-row-amount placeholder="Ej: 1.680" value="' + escapeHTML(String(amount)) + '" />'
        +   '</label>'
        +   '<label class="sr-field"><span class="sr-field__label">Nº acciones</span>'
        +     '<input type="text" inputmode="decimal" data-row-shares placeholder="Ej: 100" value="' + escapeHTML(String(shares)) + '" />'
        +   '</label>'
        +   '<button type="button" class="sr-icon-btn sr-icon-btn--row" data-row-remove title="Quitar movimiento" aria-label="Quitar movimiento">×</button>'
        + '</div>';
    }

    function bindLedger(wrap, opts) {
      opts = opts || {};
      var editing = !!opts.editing;
      var editingIdx = opts.editingIdx;
      var rowsEl    = wrap.querySelector('[data-ledger-rows]');
      var addBtn    = wrap.querySelector('[data-ledger-add]');
      var cancelBtn = wrap.querySelector('[data-ledger-cancel]');
      var saveBtn   = wrap.querySelector('[data-ledger-save]');
      var modeOpts  = wrap.querySelectorAll('[data-mode-opt]');
      var movementsEl = wrap.querySelector('[data-ledger-movements]');
      var currentMode = (saveBtn && saveBtn.getAttribute('data-mode')) || 'watch';

      function refresh() {
        if (currentMode === 'watch') return; // sin cálculos en modo seguimiento
        var sym = (activePick && activePick.currency) || '€';
        var txs = collectTransactions(rowsEl);
        var r = recompute(txs);
        var setText = function (sel, txt) { var el = wrap.querySelector(sel); if (el) el.textContent = txt; };
        setText('[data-sum-net]', isFinite(r.net_shares) ? formatNumber(r.net_shares, r.net_shares % 1 === 0 ? 0 : 4) : '—');
        setText('[data-sum-avg]', r.avg_price > 0 ? formatMoney(r.avg_price, sym) : '—');
        setText('[data-sum-invested]', r.invested > 0 ? formatMoney(r.invested, sym) : '—');
      }

      function setMode(mode) {
        currentMode = mode;
        modeOpts.forEach(function (el) {
          el.classList.toggle('is-active', el.getAttribute('data-mode-opt') === mode);
          var radio = el.querySelector('input[type=radio]');
          if (radio) radio.checked = (el.getAttribute('data-mode-opt') === mode);
        });
        var watch = mode === 'watch';
        // Paso B (movimientos) solo visible en modo "Con importes".
        if (movementsEl) movementsEl.style.display = watch ? 'none' : '';
        if (saveBtn) {
          saveBtn.setAttribute('data-mode', mode);
          saveBtn.textContent = editing
            ? (watch ? 'Guardar como solo seguimiento' : 'Guardar movimientos')
            : (watch ? 'Añadir a mi cartera' : 'Añadir a mi cartera');
        }
      }

      modeOpts.forEach(function (el) {
        el.addEventListener('click', function () {
          setMode(el.getAttribute('data-mode-opt'));
        });
      });

      function bindRow(row) {
        row.querySelectorAll('input, select').forEach(function (el) {
          el.addEventListener('input', refresh);
          el.addEventListener('change', refresh);
        });
        var rm = row.querySelector('[data-row-remove]');
        if (rm) rm.addEventListener('click', function () {
          if (rowsEl.querySelectorAll('[data-row]').length <= 1) return;
          row.parentNode.removeChild(row);
          refresh();
        });
      }

      rowsEl.querySelectorAll('[data-row]').forEach(bindRow);
      refresh();
      // Sincroniza visibilidad del Paso B + etiqueta del botón con el modo inicial.
      setMode(currentMode);

      if (addBtn) addBtn.addEventListener('click', function () {
        var tmp = document.createElement('div');
        tmp.innerHTML = renderLedgerRowHTML(rowsEl.children.length);
        var row = tmp.firstChild;
        rowsEl.appendChild(row);
        bindRow(row);
        refresh();
      });

      if (cancelBtn) cancelBtn.addEventListener('click', function () {
        wrap.style.display = 'none';
        wrap.innerHTML = '';
        activePick = null;
        if (searchInput) searchInput.focus();
      });

      if (saveBtn) saveBtn.addEventListener('click', function () {
        var errEl = wrap.querySelector('[data-ledger-error]');
        function showErr(msg, allowHTML) {
          if (!errEl) return;
          if (allowHTML) errEl.innerHTML = msg;
          else errEl.textContent = msg;
          errEl.style.display = '';
        }
        if (!activePick) return;
        // En modo NO edición: bloquear si ya existe duplicado (en edición sí está permitido).
        if (!editing) {
          var dup = state.positions.find(function (p) { return p.ticker === activePick.ticker; });
          if (dup) {
            showErr(
              'Ya tienes <strong>' + escapeHTML(activePick.ticker) + '</strong> en tu cartera. ' +
              'Para añadir más compras o ventas, ve a <a href="/pages/mi-cartera">Mi cartera</a>.',
              true
            );
            return;
          }
          if (state.positions.length >= maxPositions) {
            showErr(root.getAttribute('data-max-warning') || 'Has alcanzado el máximo.');
            return;
          }
        }

        var pos;
        if (currentMode === 'watch') {
          // R5 · Modo solo seguimiento: posición sin importes
          pos = {
            ticker: activePick.ticker,
            handle: activePick.handle,
            product_gid: activePick.product_gid,
            name: activePick.name,
            mode: 'watch',
            transactions: [],
            net_shares: 0,
            avg_price: 0,
            invested: 0,
            updated_at: new Date().toISOString()
          };
        } else {
          var txs = collectTransactions(rowsEl);
          if (!txs.length) { showErr('Añade al menos un movimiento, o cambia a "Solo seguimiento" si aún no quieres meter importes.'); return; }
          // Fecha es opcional (no bloquea el alta). Solo exigimos shares > 0 y, en compras, amount > 0.
          var invalid = txs.find(function (t) {
            if (!isFinite(t.shares) || t.shares <= 0) return true;
            if (t.type === 'buy' && (!isFinite(t.amount) || t.amount <= 0)) return true;
            return false;
          });
          if (invalid) { showErr('Revisa los movimientos: cada compra necesita importe y nº de acciones > 0.'); return; }
          var calc = recompute(txs);
          if (calc.net_shares < 0) { showErr('Las ventas superan a las compras. Revisa los movimientos.'); return; }
          pos = {
            ticker: activePick.ticker,
            handle: activePick.handle,
            product_gid: activePick.product_gid,
            name: activePick.name,
            mode: 'positions',
            transactions: txs.map(function (t) { return { type: t.type, date: t.date, amount: t.amount, shares: t.shares }; }),
            net_shares: calc.net_shares,
            avg_price: calc.avg_price,
            invested: calc.invested,
            realized_pl: calc.realized_pl,
            total_buy_amount: calc.total_buy_amount,
            total_sell_amount: calc.total_sell_amount,
            sold_shares: calc.sold_shares,
            updated_at: new Date().toISOString()
          };
        }

        if (editing && typeof editingIdx === 'number' && editingIdx >= 0) {
          // Reemplazar la posición existente
          state.positions[editingIdx] = pos;
          track('position_edited', { ticker: pos.ticker, mode: pos.mode });
        } else {
          // Nueva posición
          state.positions.push(pos);
          track('position_added', { ticker: pos.ticker, mode: pos.mode });
        }
        state.pending_sync = true;
        saveState(state);

        // Limpiar ledger y refrescar estado UI
        wrap.style.display = 'none';
        wrap.innerHTML = '';
        activePick = null;
        if (searchInput) { searchInput.value = ''; searchInput.focus(); }
        renderState();
      });
    }

    function collectTransactions(rowsEl) {
      var out = [];
      rowsEl.querySelectorAll('[data-row]').forEach(function (row) {
        out.push({
          type: row.querySelector('[data-row-type]').value,
          date: row.querySelector('[data-row-date]').value,
          amount: parseNumberES(row.querySelector('[data-row-amount]').value),
          shares: parseNumberES(row.querySelector('[data-row-shares]').value)
        });
      });
      return out;
    }

    // ── Coverage request (empresa no cubierta)
    function openCoverageRequest(seed) {
      if (!coverageWrap) return;
      coverageWrap.innerHTML = ''
        + '<div class="sr-coverage">'
        +   '<div class="sr-coverage__head"><strong>Solicitar cobertura</strong>'
        +     '<button type="button" class="sr-icon-btn" data-cov-cancel title="Cancelar">×</button>'
        +   '</div>'
        +   '<label class="sr-field"><span class="sr-field__label">Empresa</span>'
        +     '<input type="text" data-cov-name placeholder="Nombre de la empresa" value="' + escapeHTML(seed || '') + '" />'
        +   '</label>'
        +   '<label class="sr-field"><span class="sr-field__label">Ticker (opcional)</span>'
        +     '<input type="text" data-cov-ticker placeholder="Ej: ABC" maxlength="12" />'
        +   '</label>'
        +   '<div class="sr-coverage__actions">'
        +     '<button type="button" class="sr-btn sr-btn--primary" data-cov-save>Solicitar cobertura</button>'
        +   '</div>'
        + '</div>';
      coverageWrap.style.display = '';
      hideResults();
      coverageWrap.querySelector('[data-cov-cancel]').addEventListener('click', function () {
        coverageWrap.style.display = 'none';
        coverageWrap.innerHTML = '';
      });
      coverageWrap.querySelector('[data-cov-save]').addEventListener('click', function () {
        var name = coverageWrap.querySelector('[data-cov-name]').value.trim();
        var ticker = coverageWrap.querySelector('[data-cov-ticker]').value.trim().toUpperCase();
        if (!name && !ticker) return;
        // Duplicado
        var dup = state.coverage_requests.find(function (r) {
          return (ticker && r.ticker === ticker) || (name && r.name === name);
        });
        if (!dup) {
          var req = { ticker: ticker, name: name, requested_at: new Date().toISOString(), status: 'requested' };
          state.coverage_requests.push(req);
          state.pending_sync = true;
          saveState(state);
          // No sincronizamos aquí: se hará en finalizeAndGo.
          track('coverage_requested', { ticker: req.ticker, name: req.name });
        }
        coverageWrap.style.display = 'none';
        coverageWrap.innerHTML = '';
        if (searchInput) { searchInput.value = ''; searchInput.focus(); }
        renderState();
      });
    }

    // ── Render estado (cards + paso final + stepper)
    function renderState() {
      var total = state.positions.length + state.coverage_requests.length;
      // Cards de cartera (sustituye los chips)
      if (chipsEl) {
        chipsEl.innerHTML = ''
          + state.positions.map(function (p, i) { return renderPositionCardHTML(p, i, { ctx: 'onboarding' }); }).join('')
          + state.coverage_requests.map(function (r, i) { return renderRequestCardHTML(r, i); }).join('');
        renderPositionCharts(chipsEl);
        // Enriquecer con "Precio Real" + "Consenso" las posiciones cuya serie aún no tenemos.
        var needSeries = state.positions.filter(function (p) { return p.product_gid && !POSITION_SERIES[p.ticker]; });
        if (needSeries.length) {
          fetchPositionSeries(needSeries.map(function (p) { return p.product_gid; })).then(function (map) {
            Object.keys(map || {}).forEach(function (k) { POSITION_SERIES[k] = map[k]; });
            renderPositionCharts(chipsEl);
          });
        }
        chipsEl.querySelectorAll('[data-pos-remove]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var idx = parseInt(btn.getAttribute('data-pos-remove'), 10);
            state.positions.splice(idx, 1);
            saveState(state);
            // No sincronizamos aquí: el sync ocurre en finalizeAndGo.
            renderState();
          });
        });
        chipsEl.querySelectorAll('[data-req-remove]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var idx = parseInt(btn.getAttribute('data-req-remove'), 10);
            state.coverage_requests.splice(idx, 1);
            saveState(state);
            renderState();
          });
        });
      }
      // Contador
      if (counterEl) {
        if (total === 0) counterEl.textContent = 'Sin empresas en tu cartera.';
        else if (total === 1) counterEl.textContent = '1 empresa añadida';
        else counterEl.textContent = total + ' empresas añadidas';
      }
      // Aviso máximo
      if (maxWarning) maxWarning.style.display = state.positions.length >= maxPositions ? '' : 'none';

      // Alerta de cartera vacía (paso 1)
      if (emptyAlertEl) emptyAlertEl.style.display = total === 0 ? '' : 'none';

      var canAdvance = total > 0;

      // CTA "Continuar" del paso 1
      if (nextBtn) {
        nextBtn.disabled = !canAdvance;
      }
      if (nextHint) {
        nextHint.textContent = canAdvance
          ? 'Listo. ' + (customerId ? 'Pasa a confirmar tu cartera.' : 'Pasa a crear tu cuenta.')
          : 'Añade al menos 1 empresa para continuar.';
      }

      // CTA "Entrar a Mi cartera" del paso 3
      if (confirmCta) confirmCta.disabled = !canAdvance;

      // Si estamos en paso 3, refrescar el resumen
      if (currentStep === 3) renderSummary();

      // Actualiza stepper visualmente
      updateStepper();
    }

    function renderRequestCardHTML(r, idx) {
      var label = r.ticker || r.name;
      return ''
        + '<article class="sr-pos sr-pos--request" data-req="' + idx + '">'
        +   '<header class="sr-pos__head">'
        +     '<div class="sr-pos__head-id">'
        +       '<span class="sr-pill sr-pill--soft">' + escapeHTML(label) + '</span>'
        +       '<div>'
        +         '<h3 class="sr-pos__name">' + escapeHTML(r.name || r.ticker) + '</h3>'
        +         '<p class="sr-pos__sub">Cobertura solicitada — te avisamos cuando la incorporemos al seguimiento</p>'
        +       '</div>'
        +     '</div>'
        +     '<button type="button" class="sr-pos__remove" data-req-remove="' + idx + '" aria-label="Quitar ' + escapeHTML(label) + '">×</button>'
        +   '</header>'
        + '</article>';
    }

    // ── Render resumen del paso 3 (KPI tiles + lista compacta)
    function renderSummary() {
      var total = state.positions.length + state.coverage_requests.length;
      var withImports = state.positions.filter(function (p) { return p.mode !== 'watch' && p.net_shares > 0; }).length;
      var watchOnly = state.positions.filter(function (p) { return p.mode === 'watch' || !p.net_shares; }).length;

      // KPI tiles
      var kpiTotal = root.querySelector('[data-sr-kpi-total]');
      var kpiPos = root.querySelector('[data-sr-kpi-positions]');
      var kpiWatch = root.querySelector('[data-sr-kpi-watch]');
      if (kpiTotal) kpiTotal.textContent = String(total);
      if (kpiPos) kpiPos.textContent = String(withImports);
      if (kpiWatch) kpiWatch.textContent = String(watchOnly);

      if (!summaryListEl) return;
      if (summaryCountEl) summaryCountEl.textContent = String(total);

      var html = '';
      state.positions.forEach(function (p) {
        var detail;
        if (p.mode === 'watch' || !p.net_shares) {
          detail = '<span class="sr-summary-row__tag sr-summary-row__tag--watch">Solo seguimiento</span>';
        } else {
          var sharesStr = formatNumber(p.net_shares, p.net_shares % 1 === 0 ? 0 : 4);
          var richSum = findRich(p.ticker);
          var symSum = (richSum && richSum.currency) || '€';
          detail = '<span class="sr-summary-row__detail">' + escapeHTML(sharesStr) + ' acc. · ' + formatMoney(p.invested || 0, symSum) + '</span>';
        }
        html += ''
          + '<li class="sr-summary-row" data-mode="' + escapeHTML(p.mode || 'positions') + '">'
          +   '<span class="sr-pill sr-pill--solid">' + escapeHTML(p.ticker) + '</span>'
          +   '<span class="sr-summary-row__name">' + escapeHTML(p.name) + '</span>'
          +   detail
          + '</li>';
      });
      state.coverage_requests.forEach(function (r) {
        var label = r.ticker || r.name;
        html += ''
          + '<li class="sr-summary-row sr-summary-row--request">'
          +   '<span class="sr-pill sr-pill--soft">' + escapeHTML(label) + '</span>'
          +   '<span class="sr-summary-row__name">' + escapeHTML(r.name || r.ticker) + '</span>'
          +   '<span class="sr-summary-row__tag sr-summary-row__tag--request">Cobertura solicitada</span>'
          + '</li>';
      });
      summaryListEl.innerHTML = html;
      if (summaryEmptyEl) summaryEmptyEl.style.display = total === 0 ? '' : 'none';
    }

    if (confirmCta) {
      confirmCta.addEventListener('click', function (e) {
        e.preventDefault();
        if (confirmCta.getAttribute('aria-disabled') === 'true') return;
        track('free_continue_click', { positions_count: state.positions.length });
        finalizeAndGo();
      });
    }

    // ── Oferta de prueba 15 días por 5€ (paso 3)
    var trialEl = root.querySelector('[data-sr-trial]');
    var trialCta = root.querySelector('[data-sr-trial-cta]');
    var trialTitleEl = root.querySelector('[data-sr-trial-title]');
    var trialLeadEl = root.querySelector('[data-sr-trial-lead]');
    var trialOfferViewedFired = false;

    function topInvestedTickers(positions, n) {
      var copy = (positions || []).filter(function (p) { return p && p.ticker; }).slice();
      copy.sort(function (a, b) { return (b.invested || 0) - (a.invested || 0); });
      return copy.slice(0, n).map(function (p) { return p.ticker; });
    }

    function renderTrialPersonalization() {
      if (!trialEl) return;
      var total = state.positions.length;
      var top = topInvestedTickers(state.positions, 3);
      // Si hay más, añadimos un "y N más"
      var tickerLabel;
      if (top.length === 0) {
        tickerLabel = 'tus empresas';
      } else if (state.positions.length > top.length) {
        tickerLabel = top.join(', ') + ' y ' + (state.positions.length - top.length) + ' más';
      } else {
        tickerLabel = top.length === 1
          ? top[0]
          : top.slice(0, -1).join(', ') + ' y ' + top[top.length - 1];
      }
      if (trialTitleEl) {
        trialTitleEl.textContent = (trialTitleEl.getAttribute('data-template') || trialTitleEl.textContent)
          .replace('{N}', total);
        // Guardar template original la primera vez
        if (!trialTitleEl.getAttribute('data-template')) {
          trialTitleEl.setAttribute('data-template', trialTitleEl.textContent);
        }
      }
      if (trialLeadEl) {
        var leadTpl = trialLeadEl.getAttribute('data-template') || trialLeadEl.textContent;
        if (!trialLeadEl.getAttribute('data-template')) {
          trialLeadEl.setAttribute('data-template', leadTpl);
        }
        trialLeadEl.textContent = leadTpl.replace('{TICKERS}', tickerLabel);
      }
    }

    function startTrialCheckout() {
      if (!trialCta) return;
      var variantId = trialEl.getAttribute('data-trial-variant-id') || '';
      if (!variantId) {
        try { console.warn('[trial] sin variant ID configurado'); } catch (_) {}
        return;
      }
      track('trial_offer_click', { variant: variantId, positions_count: state.positions.length });

      trialCta.disabled = true;
      trialCta.classList.add('sr-btn--loading');
      var originalLabel = trialCta.innerHTML;
      trialCta.innerHTML = 'Preparando tu cartera y checkout…';

      // 1) Guardar la cartera primero (la prueba no debe perder lo que el user montó).
      // 2) clear cart → add variant → begin_checkout (GA4) → /checkout.
      ensurePortfolioSaved()
        .then(function () {
          return clearCartAddAndCheckout(variantId, {
            surface: 'onboarding-step-3',
            positions_count: state.positions.length,
            price: 5.00,
            currency: 'EUR'
          });
        })
        .catch(function (err) {
          try { console.error('[trial] error', err); } catch (_) {}
          trialCta.disabled = false;
          trialCta.classList.remove('sr-btn--loading');
          trialCta.innerHTML = originalLabel;
          // Fallback duro: link permalink cart con la variante ya pre-puesta.
          try { window.location.href = '/cart/' + encodeURIComponent(variantId) + ':1'; } catch (_) {}
        });
    }

    // ensurePortfolioSaved: ejecuta los syncs de finalizeAndGo SIN redirigir a Mi cartera.
    // Reutiliza la misma lógica de syncs secuenciales con snapshots para Make.
    function ensurePortfolioSaved() {
      var customerIdNum = (customerId || '').toString().replace(/[^0-9]/g, '');
      if (!customerIdNum) return Promise.resolve();
      if (!state.positions.length && !state.coverage_requests.length) return Promise.resolve();

      var positionsSnapshot = buildPositionsSnapshot(state.positions);
      var requestsSnapshot = buildRequestsSnapshot(state.coverage_requests);
      var thunks = [];

      state.positions.forEach(function (p) {
        thunks.push(function () {
          return syncPortfolio({
            action: 'upsert_position',
            customer_id: customerIdNum,
            ticker: p.ticker,
            product_gid: p.product_gid,
            transactions: p.transactions,
            net_shares: p.net_shares,
            avg_price: p.avg_price,
            invested: p.invested,
            positions: positionsSnapshot
          });
        });
      });
      state.coverage_requests.forEach(function (r) {
        thunks.push(function () {
          return syncPortfolio({
            action: 'request_coverage',
            customer_id: customerIdNum,
            ticker: r.ticker,
            name: r.name,
            requests: requestsSnapshot
          });
        });
      });

      var INTER_CALL_DELAY_MS = 1200;
      return thunks.reduce(function (chain, thunk, idx) {
        return chain.then(function () {
          var delay = idx === 0 ? 0 : INTER_CALL_DELAY_MS;
          return new Promise(function (res) { setTimeout(res, delay); }).then(thunk).catch(function () {});
        });
      }, Promise.resolve()).then(function () {
        var s = loadState();
        s.pending_sync = false;
        saveState(s);
      });
    }

    if (trialCta) {
      trialCta.addEventListener('click', function (e) {
        e.preventDefault();
        startTrialCheckout();
      });
    }

    // ── R8 · Password strength meter
    var pwdInput = registerStep ? registerStep.querySelector('[data-sr-password]') : null;
    var pwdConfirmInput = registerStep ? registerStep.querySelector('[data-sr-password-confirm]') : null;
    var pwdMatchBox = registerStep ? registerStep.querySelector('#sr-pwd-match') : null;
    var pwdStrengthBox = registerStep ? registerStep.querySelector('#sr-pwd-strength') : null;
    var pwdStrengthFill = registerStep ? registerStep.querySelector('[data-sr-pwd-fill]') : null;
    var pwdStrengthLabel = registerStep ? registerStep.querySelector('[data-sr-pwd-label]') : null;
    if (pwdInput && pwdStrengthBox) {
      pwdInput.addEventListener('input', function () {
        var v = pwdInput.value || '';
        if (!v.length) {
          pwdStrengthBox.hidden = true;
        } else {
          pwdStrengthBox.hidden = false;
          // Score 0-3: longitud + variedad
          var score = 0;
          var classes = 0;
          if (/[a-z]/.test(v)) classes++;
          if (/[A-Z]/.test(v)) classes++;
          if (/\d/.test(v)) classes++;
          if (/[^a-zA-Z0-9]/.test(v)) classes++;
          if (v.length >= 8 && classes >= 1) score = 1;
          if (v.length >= 10 && classes >= 2) score = 2;
          if (v.length >= 12 && classes >= 3) score = 3;
          var labels = ['Muy débil', 'Aceptable', 'Sólida', 'Excelente'];
          var widths = ['25%', '50%', '75%', '100%'];
          if (pwdStrengthFill) {
            pwdStrengthFill.style.width = widths[score];
            pwdStrengthFill.setAttribute('data-level', String(score === 0 ? 1 : (score + 1)));
          }
          if (pwdStrengthLabel) pwdStrengthLabel.textContent = labels[score];
        }
        updatePwdMatch();
      });
    }

    // ── Validación de coincidencia de contraseñas
    function updatePwdMatch() {
      if (!pwdInput || !pwdConfirmInput || !pwdMatchBox) return;
      var a = pwdInput.value || '';
      var b = pwdConfirmInput.value || '';
      if (!b.length) {
        pwdMatchBox.hidden = true;
        pwdMatchBox.classList.remove('is-ok', 'is-error');
        pwdConfirmInput.removeAttribute('aria-invalid');
        return;
      }
      pwdMatchBox.hidden = false;
      if (a === b) {
        pwdMatchBox.classList.add('is-ok');
        pwdMatchBox.classList.remove('is-error');
        pwdMatchBox.innerHTML = '<span class="sr-pwd-match__icon" aria-hidden="true">✓</span> Las contraseñas coinciden';
        pwdConfirmInput.removeAttribute('aria-invalid');
      } else {
        pwdMatchBox.classList.add('is-error');
        pwdMatchBox.classList.remove('is-ok');
        pwdMatchBox.innerHTML = '<span class="sr-pwd-match__icon" aria-hidden="true">✗</span> Las contraseñas no coinciden';
        pwdConfirmInput.setAttribute('aria-invalid', 'true');
      }
    }
    if (pwdConfirmInput) {
      pwdConfirmInput.addEventListener('input', updatePwdMatch);
    }

    // ── Toggle "ver contraseña" en cada input
    var pwdToggles = registerStep ? registerStep.querySelectorAll('[data-sr-pwd-toggle]') : [];
    pwdToggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var wrap = btn.closest('.sr-pwd-wrap');
        if (!wrap) return;
        var input = wrap.querySelector('input');
        if (!input) return;
        var showing = input.type === 'text';
        input.type = showing ? 'password' : 'text';
        btn.setAttribute('aria-pressed', showing ? 'false' : 'true');
        btn.setAttribute('aria-label', showing ? 'Mostrar contraseña' : 'Ocultar contraseña');
        wrap.classList.toggle('is-showing', !showing);
      });
    });

    // ── Intercepción del form de registro nativo (create_customer)
    // El form de Shopify redirige por defecto a /account tras éxito, lo que rompería
    // el flujo (no volveríamos nunca a /pages/onboarding para sincronizar).
    // Hacemos POST AJAX al mismo endpoint y, en éxito, redirigimos manualmente a
    // /pages/onboarding?finalize=1 (donde el JS detectará customerId + posiciones
    // y disparará finalizeAndGo() → /pages/mi-cartera).
    var registerForm = registerStep ? registerStep.querySelector('form') : null;
    if (registerForm) {
      var privacyInput = registerForm.querySelector('[data-sr-privacy]');
      var marketingInput = registerForm.querySelector('[data-sr-marketing]');
      var submitBtn = registerForm.querySelector('[data-sr-register-submit]');

      // Validación bloqueante. Devuelve true si está OK para continuar.
      function validateRegisterForm() {
        // 1) Privacidad obligatoria (máxima prioridad legal)
        if (privacyInput && !privacyInput.checked) {
          showRegisterError('Necesitas aceptar la política de privacidad y los términos de uso para continuar.', false);
          try { privacyInput.focus(); } catch (_) {}
          return false;
        }
        // 2) Coincidencia de contraseñas
        if (pwdInput && pwdConfirmInput) {
          var a = pwdInput.value || '';
          var b = pwdConfirmInput.value || '';
          if (a !== b) {
            updatePwdMatch();
            try { pwdConfirmInput.focus(); } catch (_) {}
            return false;
          }
        }
        return true;
      }

      // Submit del form: SIEMPRE bloqueado. El form nunca se envía nativamente.
      // Se dispara solo por Enter dentro de un input; redirigimos al click del botón.
      registerForm.addEventListener('submit', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (submitBtn && !submitBtn.disabled) submitBtn.click();
        return false;
      });

      // Único punto de entrada: click en el botón.
      // El botón es type="button" → no envía el form automáticamente.
      // Tras validar, cambiamos a type="submit" y disparamos el envío NATIVO del browser.
      // Esto es crucial: el endpoint /account de Shopify rechaza peticiones AJAX (CORS)
      // con 400 Bad Request. Solo acepta navegaciones nativas (Sec-Fetch-Mode: navigate).
      if (submitBtn) {
        submitBtn.addEventListener('click', function () {
          if (!validateRegisterForm()) return;
          // Log de consent (Make / Brevo) — síncrono, antes de la navegación
          try {
            track('consent_given', {
              accepts_marketing: !!(marketingInput && marketingInput.checked),
              accepts_privacy: !!(privacyInput && privacyInput.checked),
              consent_text_marketing: 'Sí, quiero recibir: el resumen semanal de hechos relevantes de mi cartera, el brief del lunes de oportunidades, el email semanal con la tesis destacada de la semana y comunicaciones comerciales de Stocks Report. Cancelo desde cualquier email.',
              consent_text_privacy: 'He leído y acepto la política de privacidad y los términos de uso.',
              timestamp: new Date().toISOString()
            });
          } catch (_) {}
          // Flag para detectar al cargar /pages/onboarding?step=3 que venimos de un registro nuevo
          try { sessionStorage.setItem('sr_register_pending', '1'); } catch (_) {}
          // UI de carga
          submitBtn.disabled = true;
          submitBtn.classList.add('sr-btn--loading');
          // Cambiar a submit y enviar nativamente: Shopify procesa, crea cuenta y redirige a return_to
          submitBtn.setAttribute('type', 'submit');
          // Pequeño delay para que el atributo se aplique antes del submit
          setTimeout(function () {
            try {
              if (typeof registerForm.requestSubmit === 'function') {
                registerForm.requestSubmit(submitBtn);
              } else {
                registerForm.submit();
              }
            } catch (_) {
              registerForm.submit();
            }
          }, 0);
        });
      }
    }

    // Si llegamos al onboarding con sr_register_pending y customerId → la cuenta acaba de crearse.
    // Disparamos el evento account_created (no lo podíamos hacer durante el submit nativo).
    try {
      if (customerId && sessionStorage.getItem('sr_register_pending') === '1') {
        track('account_created', { positions_count: state.positions.length });
        sessionStorage.removeItem('sr_register_pending');
      }
    } catch (_) {}

    function showRegisterError(msg, allowHTML) {
      var errBox = registerStep.querySelector('.sr-form-errors');
      if (!errBox) {
        errBox = document.createElement('div');
        errBox.className = 'sr-form-errors';
        errBox.setAttribute('role', 'alert');
        registerStep.querySelector('form').insertBefore(errBox, registerStep.querySelector('form').firstChild);
      }
      errBox.innerHTML = '<div class="sr-form-error">' + (allowHTML ? msg : escapeHTML(msg)) + '</div>';
      errBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function finalizeAndGo() {
      var target = root.getAttribute('data-mi-cartera-url') || '/pages/mi-cartera';

      // Solo sincronizar con customer_id. Sin sesión = no se llama a Make.
      var customerIdNum = (customerId || '').toString().replace(/[^0-9]/g, '');
      if (!customerIdNum) {
        // Sin sesión y sin nada que sincronizar: solo redirigir.
        window.location.assign(target);
        return;
      }

      // Nada que guardar: ir directo.
      if (!state.positions.length && !state.coverage_requests.length) {
        window.location.assign(target);
        return;
      }

      // UI loading sobre el CTA del paso 3
      var confirmEl = root.querySelector('[data-sr-confirm]');
      var prevText = confirmEl ? confirmEl.innerHTML : '';
      function setBusy(busy, label) {
        if (!confirmEl) return;
        confirmEl.disabled = !!busy;
        confirmEl.classList.toggle('sr-btn--loading', !!busy);
        if (busy && label) confirmEl.innerHTML = label;
        if (!busy) confirmEl.innerHTML = prevText;
      }
      function clearFinalizeError() {
        var box = root.querySelector('[data-sr-finalize-error]');
        if (box) box.style.display = 'none';
      }
      function showFinalizeError(msg) {
        var box = root.querySelector('[data-sr-finalize-error]');
        if (!box) {
          box = document.createElement('div');
          box.setAttribute('data-sr-finalize-error', '');
          box.className = 'sr-finalize-error';
          box.setAttribute('role', 'alert');
          if (confirmEl && confirmEl.parentNode) {
            confirmEl.parentNode.insertBefore(box, confirmEl);
          }
        }
        box.innerHTML = msg;
        box.style.display = '';
        box.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      function showFinalizeSuccess() {
        var msg = root.getAttribute('data-success-message');
        if (!msg) return;
        var box = root.querySelector('[data-sr-finalize-success]');
        if (!box) {
          box = document.createElement('div');
          box.setAttribute('data-sr-finalize-success', '');
          box.className = 'sr-finalize-success';
          box.setAttribute('role', 'status');
          if (confirmEl && confirmEl.parentNode) {
            confirmEl.parentNode.insertBefore(box, confirmEl);
          }
        }
        box.textContent = msg;
        box.style.display = '';
        box.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      clearFinalizeError();
      setBusy(true, 'Preparando tu cartera…');

      // Tras la última llamada a Make, esperamos POST_LAST_CALL_MS antes de redirigir
      // para dar margen a que el escenario de Make termine de escribir en background.
      var POST_LAST_CALL_MS = 3000;
      var startedAt = Date.now();

      // Snapshots completos de la cartera (Make los usa para reconstruir el metafield del customer)
      var positionsSnapshot = buildPositionsSnapshot(state.positions);
      var requestsSnapshot = buildRequestsSnapshot(state.coverage_requests);

      // Construir THUNKS (funciones que devuelven Promise). Se ejecutarán secuencialmente
      // para evitar race conditions en Make (escenarios concurrentes pisándose el metafield).
      var jobThunks = [];
      var positionTickers = [];
      var requestLabels = [];

      state.positions.forEach(function (p) {
        positionTickers.push(p.ticker);
        jobThunks.push(function () {
          return syncPortfolio({
            action: 'upsert_position',
            customer_id: customerIdNum,
            ticker: p.ticker,
            product_gid: p.product_gid,
            transactions: p.transactions,
            net_shares: p.net_shares,
            avg_price: p.avg_price,
            invested: p.invested,
            positions: positionsSnapshot
          });
        });
      });
      state.coverage_requests.forEach(function (r) {
        requestLabels.push(r.ticker || r.name);
        jobThunks.push(function () {
          return syncPortfolio({
            action: 'request_coverage',
            customer_id: customerIdNum,
            ticker: r.ticker,
            name: r.name,
            requests: requestsSnapshot
          });
        });
      });

      // Ejecución SECUENCIAL con delay entre llamadas.
      // Make procesa los escenarios de forma asíncrona; dos llamadas en paralelo a la misma
      // cuenta producen race conditions sobre custom.portfolio (un escenario sobreescribe al otro).
      // Esperar ~1.2s entre llamadas da margen a Make para terminar el primero antes del siguiente.
      var INTER_CALL_DELAY_MS = 1200;
      function runSequentially(thunks) {
        var results = [];
        return thunks.reduce(function (chain, thunk, idx) {
          return chain.then(function () {
            var delay = idx === 0 ? 0 : INTER_CALL_DELAY_MS;
            return new Promise(function (r) { setTimeout(r, delay); })
              .then(thunk)
              .then(function (v) { results.push({ status: 'fulfilled', value: v }); })
              .catch(function (e) { results.push({ status: 'rejected', reason: e }); });
          });
        }, Promise.resolve()).then(function () { return results; });
      }

      var settle = runSequentially(jobThunks);

      // Hard limit: jobs.length * (1.2s delay + ~1s fetch) + buffer = ~25s + buffer.
      var totalCalls = jobThunks.length;
      var hardLimitMs = Math.max(25000, totalCalls * 2500 + 5000);
      var hardLimit = new Promise(function (resolve) {
        setTimeout(function () { resolve('__timeout__'); }, hardLimitMs);
      });

      Promise.race([settle, hardLimit]).then(function (result) {
        if (result === '__timeout__') {
          setBusy(false);
          showFinalizeError(
            '<strong>Estamos tardando más de lo normal.</strong> Tu cartera está guardada en este dispositivo. ' +
            'Pulsa de nuevo "Entrar a Mi cartera" para reintentar.'
          );
          try { console.warn('[finalizeAndGo] timeout 20s'); } catch (_) {}
          return;
        }
        var allOk = Array.isArray(result) && result.every(function (r) {
          return r.status === 'fulfilled' && r.value && r.value.ok;
        });
        if (allOk) {
          var s = loadState();
          s.pending_sync = false;
          saveState(s);
          try { console.info('[finalizeAndGo] all OK, waiting', POST_LAST_CALL_MS, 'ms before redirect'); } catch (_) {}
          // Refuerzo del acceso completo antes de redirigir (Superficie C).
          setBusy(true, 'Entrando a Mi cartera…');
          showFinalizeSuccess();
          // 3s tras la última llamada para que Make termine de procesar en background.
          setTimeout(function () { window.location.assign(target); }, POST_LAST_CALL_MS);
          return;
        }
        // Alguno falló: detallamos qué (si podemos) y ofrecemos reintentar.
        var failedIdx = [];
        result.forEach(function (r, i) {
          if (!(r.status === 'fulfilled' && r.value && r.value.ok)) failedIdx.push(i);
        });
        var failedLabels = failedIdx.map(function (i) {
          if (i < positionTickers.length) return positionTickers[i];
          return requestLabels[i - positionTickers.length];
        }).filter(Boolean);
        var listHtml = failedLabels.length
          ? '<br>No se han guardado: <strong>' + failedLabels.map(escapeHTML).join(', ') + '</strong>.'
          : '';
        setBusy(false);
        showFinalizeError(
          '<strong>No se ha podido guardar tu cartera.</strong>' + listHtml +
          '<br>Tu cartera está a salvo en este dispositivo. Pulsa de nuevo "Entrar a Mi cartera" para reintentar.'
        );
        try { console.warn('[finalizeAndGo] failed:', result); } catch (_) {}
      }).catch(function (err) {
        setBusy(false);
        showFinalizeError(
          '<strong>No se ha podido guardar tu cartera.</strong> Revisa tu conexión y pulsa de nuevo "Entrar a Mi cartera" para reintentar.'
        );
        try { console.error('[finalizeAndGo] catastrophic', err); } catch (_) {}
      });
    }
  }

  // ─── Mi cartera UI ───────────────────────────────────────────────────────
  function bootMiCartera(root) {
    var customerId = root.getAttribute('data-customer-id') || '';

    var legacyWebhook = (root.getAttribute('data-webhook-url') || '').trim();
    window.SR_PORTFOLIO_WEBHOOKS = {
      upsert_position:  (root.getAttribute('data-webhook-upsert')  || legacyWebhook || '').trim(),
      remove_position:  (root.getAttribute('data-webhook-remove')  || '').trim(),
      request_coverage: (root.getAttribute('data-webhook-request') || '').trim()
    };

    // Snapshot actual de la cartera leído del DOM (Make lo necesita en cada acción).
    // `excludeTicker` permite producir la lista "post-acción" (tras remove).
    function currentPositionsSnapshot(excludeTicker) {
      var out = [];
      root.querySelectorAll('[data-position]').forEach(function (card) {
        var t = card.getAttribute('data-ticker');
        if (!t || t === excludeTicker) return;
        out.push({ ticker: t, product_gid: card.getAttribute('data-product-gid') || '' });
      });
      return out;
    }

    // Render compartido de las cards: lee window.SR_SERVER_PORTFOLIO y rellena [data-sr-positions].
    // Usa la misma función que el onboarding para garantizar look idéntico.
    var positionsContainer = root.querySelector('[data-sr-positions]');
    var serverPortfolio = Array.isArray(window.SR_SERVER_PORTFOLIO) ? window.SR_SERVER_PORTFOLIO : [];

    var isPremium = !!window.SR_IS_PREMIUM;

    function renderCardsFromServer() {
      if (!positionsContainer) return;
      positionsContainer.innerHTML = serverPortfolio
        .map(function (p, i) {
          return renderPositionCardHTML(p, i, { ctx: 'mi-cartera', isPremium: isPremium });
        })
        .join('');
      wireCardHandlers();
      wireUnlockHandlers();
      updateSummaryFromDOM();
      renderPositionCharts(positionsContainer);
    }

    // Handler de cualquier [data-sr-unlock]: lleva al registro gratis (enfoque-free).
    function wireUnlockHandlers() {
      root.querySelectorAll('[data-sr-unlock]').forEach(function (btn) {
        if (btn.__srWired) return; btn.__srWired = true;
        btn.addEventListener('click', function () {
          var location = btn.getAttribute('data-unlock-location') || 'unknown';
          track('premium_unlock_click', { surface: 'mi-cartera', location: location });
          btn.disabled = true;
          btn.classList.add('sr-btn--loading');
          window.location.href = '/pages/onboarding';
        });
      });
    }

    track('landed_mi_cartera', {
      logged_in: !!customerId,
      positions_count: serverPortfolio.length
    });

    // Banner de oferta prueba 5€ (siempre visible para users free, sin opción de descartar).
    var trialBanner = root.querySelector('[data-sr-trial-banner]');
    if (trialBanner) {
      var bannerVariantId = trialBanner.getAttribute('data-trial-variant-id') || '';
      track('trial_banner_viewed', { surface: 'mi-cartera', variant: bannerVariantId });

      var trialBannerCta = trialBanner.querySelector('[data-sr-trial-banner-cta]');
      if (trialBannerCta) {
        trialBannerCta.addEventListener('click', function () {
          track('trial_banner_click', { surface: 'mi-cartera' });
          trialBannerCta.disabled = true;
          trialBannerCta.classList.add('sr-btn--loading');
          // enfoque-free: el banner lleva al registro gratis, no a checkout.
          window.location.href = '/pages/onboarding';
        });
      }
    }

    // R6 · Si el servidor tiene posiciones del cliente, limpiamos el localStorage de onboarding.
    if (customerId && serverPortfolio.length > 0) {
      try {
        var s0 = loadState();
        if (s0.positions && s0.positions.length > 0) {
          clearState();
          try { console.info('[bootMiCartera] localStorage limpiado: server ya tiene cartera.'); } catch (_) {}
        }
      } catch (_) {}
    }

    function updateSummaryFromDOM() {
      // Pintar veredictos de relevancia (data-cur, data-mag, data-base6m si existen)
      root.querySelectorAll('[data-verdict]').forEach(function (el) {
        var cur = parseFloat(el.getAttribute('data-cur'));
        var mag = parseFloat(el.getAttribute('data-mag'));
        var base = parseFloat(el.getAttribute('data-base6m'));
        var r = relevance(cur, mag, base);
        el.classList.add('sr-verdict--' + r.level);
        var lbl = el.querySelector('[data-verdict-label]');
        if (lbl) lbl.textContent = r.text;
      });
      var afectaCount = root.querySelectorAll('[data-verdict].sr-verdict--high').length;
      var calmaCount = root.querySelectorAll('[data-verdict].sr-verdict--low').length;
      var afectaEl = root.querySelector('[data-summary-afecta]');
      var calmaEl  = root.querySelector('[data-summary-calma]');
      if (afectaEl) afectaEl.textContent = afectaCount;
      if (calmaEl)  calmaEl.textContent  = calmaCount;
    }

    // Carga coverage (Storefront API) y renderiza las cards JS.
    if (positionsContainer && serverPortfolio.length > 0) {
      // Loading state elegante mientras se traen los datos enriquecidos.
      positionsContainer.innerHTML = renderCarteraLoadingHTML(serverPortfolio.length);
      loadCoverage().then(function () {
        renderCardsFromServer();
        // Enriquecer las gráficas con "Precio Real" + "Consenso" (consulta dirigida a las posiciones).
        var gids = serverPortfolio.map(function (p) { return p.product_gid; });
        fetchPositionSeries(gids).then(function (map) {
          POSITION_SERIES = map || {};
          renderPositionCharts(positionsContainer);
        });
      });
    } else {
      // Sin cartera: aún wire handlers por si quedaran cards legacy en el DOM.
      wireCardHandlers();
      updateSummaryFromDOM();
    }

    function renderCarteraLoadingHTML(n) {
      // Skeleton cards con shimmer + mensaje elegante.
      var skeletons = '';
      var count = Math.min(Math.max(n || 2, 1), 3);
      for (var i = 0; i < count; i++) {
        skeletons += ''
          + '<div class="sr-cartera-skeleton" aria-hidden="true">'
          +   '<div class="sr-cartera-skeleton__head">'
          +     '<div class="sr-cartera-skeleton__pill"></div>'
          +     '<div class="sr-cartera-skeleton__title-wrap">'
          +       '<div class="sr-cartera-skeleton__line sr-cartera-skeleton__line--name"></div>'
          +       '<div class="sr-cartera-skeleton__line sr-cartera-skeleton__line--meta"></div>'
          +     '</div>'
          +   '</div>'
          +   '<div class="sr-cartera-skeleton__body">'
          +     '<div class="sr-cartera-skeleton__col"></div>'
          +     '<div class="sr-cartera-skeleton__col"></div>'
          +     '<div class="sr-cartera-skeleton__col"></div>'
          +   '</div>'
          + '</div>';
      }
      return ''
        + '<div class="sr-cartera-loading" role="status" aria-live="polite">'
        +   '<div class="sr-cartera-loading__header">'
        +     '<div class="sr-cartera-loading__spinner" aria-hidden="true"></div>'
        +     '<div class="sr-cartera-loading__copy">'
        +       '<p class="sr-cartera-loading__title">Preparando tu cartera…</p>'
        +       '<p class="sr-cartera-loading__sub">Calculando escenarios, recorrido y señales de tus empresas.</p>'
        +     '</div>'
        +   '</div>'
        +   '<div class="sr-cartera-loading__skeletons">' + skeletons + '</div>'
        + '</div>';
    }

    // Wire de los handlers de acciones (remove-position) sobre las cards actuales.
    // Se llama tras render para que las cards JS recién insertadas queden cableadas.
    // "+ Añadir compra/venta" ahora es un <a> que lleva a /pages/onboarding?ticker=X&action=add-tx,
    // por eso aquí solo wireamos el ×.
    function wireCardHandlers() {

    // Quitar posición (server-side a través de Make)
    root.querySelectorAll('[data-remove-position]').forEach(function (btn) {
      if (btn.__srWired) return; btn.__srWired = true;
      btn.addEventListener('click', function () {
        var ticker = btn.getAttribute('data-ticker');
        showConfirmDialog({
          title: 'Quitar ' + ticker + ' de tu cartera',
          bodyHTML: 'Vas a eliminar <strong>' + escapeHTML(ticker) + '</strong> de tu cartera junto con todos los movimientos asociados. Dejarás de recibir avisos sobre esta empresa.',
          warning: 'Si más adelante quieres recuperar esta posición, tendrás que volver a buscarla y registrar de nuevo todos los movimientos.',
          confirmLabel: 'Sí, quitar de la cartera',
          cancelLabel: 'Cancelar',
          danger: true
        }).then(function (confirmed) {
          if (!confirmed) return;
          btn.disabled = true;
          var customerIdNum = (customerId || '').toString().replace(/[^0-9]/g, '');
          syncPortfolio({
            action: 'remove_position',
            customer_id: customerIdNum,
            ticker: ticker,
            positions: currentPositionsSnapshot(ticker)
          }).then(function () {
            setTimeout(function () { window.location.reload(); }, 600);
          });
        });
      });
    });

    }  // ── end wireCardHandlers

    // 5) Si custom.portfolio está vacío pero hay sr_onboarding sin sincronizar, avisamos.
    var bridge = root.querySelector('[data-bridge-pending]');
    if (bridge) {
      try {
        var s = loadState();
        if (s.positions && s.positions.length > 0 && s.pending_sync) {
          bridge.style.display = '';
        }
      } catch (_) {}
    }
  }

  // ─── Diálogo modal de confirmación (sustituye a window.confirm) ──────────
  /**
   * Muestra un <dialog> modal accesible.
   * @param opts.title        Título (texto plano)
   * @param opts.bodyHTML     Cuerpo (HTML permitido)
   * @param opts.warning      Línea de aviso destacada (texto plano)
   * @param opts.confirmLabel Texto del botón de confirmar (default: "Confirmar")
   * @param opts.cancelLabel  Texto del botón de cancelar (default: "Cancelar")
   * @param opts.danger       Si true, el botón confirmar usa el estilo danger.
   * @returns Promise<boolean> resolves true si confirma, false si cancela.
   */
  function showConfirmDialog(opts) {
    opts = opts || {};
    return new Promise(function (resolve) {
      // Implementación con <div> manual (patrón popup-email-perla) en lugar de <dialog>
      // nativo: evita bugs cross-browser donde backdrop-filter del ::backdrop afectaba
      // al contenido y daba aspecto translúcido al texto.
      var wrap = document.createElement('div');
      wrap.className = 'sr-dialog-wrap' + (opts.danger ? ' sr-dialog-wrap--danger' : '');
      wrap.setAttribute('role', 'dialog');
      wrap.setAttribute('aria-modal', 'true');
      wrap.setAttribute('aria-labelledby', 'sr-dialog-title');
      wrap.setAttribute('aria-describedby', 'sr-dialog-desc');
      wrap.innerHTML = ''
        + '<div class="sr-dialog__overlay" data-cancel-overlay></div>'
        + '<div class="sr-dialog__container" role="document">'
        +   '<div class="sr-dialog__inner">'
        +     '<div class="sr-dialog__head">'
        +       '<h2 id="sr-dialog-title" class="sr-dialog__title">' + escapeHTML(opts.title || 'Confirma la acción') + '</h2>'
        +     '</div>'
        +     '<div id="sr-dialog-desc" class="sr-dialog__body">'
        +       '<p class="sr-dialog__text">' + (opts.bodyHTML || '') + '</p>'
        +       (opts.warning
                ? '<p class="sr-dialog__warning"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg><span>' + escapeHTML(opts.warning) + '</span></p>'
                : '')
        +     '</div>'
        +     '<div class="sr-dialog__actions">'
        +       '<button type="button" class="sr-btn sr-btn--ghost" data-cancel>' + escapeHTML(opts.cancelLabel || 'Cancelar') + '</button>'
        +       '<button type="button" class="sr-btn ' + (opts.danger ? 'sr-btn--danger-solid' : 'sr-btn--primary') + '" data-confirm>' + escapeHTML(opts.confirmLabel || 'Confirmar') + '</button>'
        +     '</div>'
        +   '</div>'
        + '</div>';
      document.body.appendChild(wrap);
      document.body.classList.add('sr-dialog-open');

      var settled = false;
      function done(value) {
        if (settled) return;
        settled = true;
        wrap.classList.remove('is-active');
        document.body.classList.remove('sr-dialog-open');
        document.removeEventListener('keydown', onKey);
        setTimeout(function () {
          if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
        }, 220);
        resolve(value);
      }

      wrap.querySelector('[data-cancel]').addEventListener('click', function () { done(false); });
      wrap.querySelector('[data-cancel-overlay]').addEventListener('click', function () { done(false); });
      wrap.querySelector('[data-confirm]').addEventListener('click', function () { done(true); });

      function onKey(e) { if (e.key === 'Escape') done(false); }
      document.addEventListener('keydown', onKey);

      // Trigger de animación tras pintar
      requestAnimationFrame(function () { wrap.classList.add('is-active'); });

      // Foco en cancelar por defecto (decisión segura tras Esc / Enter)
      var cancelBtn = wrap.querySelector('.sr-dialog__actions [data-cancel]');
      if (cancelBtn) setTimeout(function () { try { cancelBtn.focus(); } catch (_) {} }, 80);
    });
  }

  // ─── Utilidades ──────────────────────────────────────────────────────────
  function escapeHTML(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }

  // ─── Bootstrap ───────────────────────────────────────────────────────────
  function boot() {
    document.querySelectorAll('[data-sr-page="onboarding"]').forEach(bootOnboarding);
    document.querySelectorAll('[data-sr-page="mi-cartera"]').forEach(bootMiCartera);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Exponer pequeñas utilidades para debugging
  window.SRPortfolio = {
    loadState: loadState,
    clearState: clearState,
    syncPortfolio: syncPortfolio,
    searchCoverage: searchCoverage,
    relevance: relevance
  };
})();
